import { useState, useEffect, useCallback } from 'react';
import { adminApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { useConfirmDialog } from '@/components/ConfirmDialog';
import { 
  Building, Building2, Users, FileText, Receipt, Briefcase, Shield, LogIn, LogOut,
  Plus, Edit, Eye, EyeOff, Activity, BarChart3, ChevronRight, CheckCircle, 
  PauseCircle, XCircle, RefreshCw, DollarSign, TrendingUp, Calendar, 
  Mail, Phone, MapPin, Crown, Search, Key, ArrowUpRight, Zap, Ban,
  HardHat, LayoutDashboard, PieChart, Clock, Settings, Send, Pencil, Trash2, X,
  Archive, RotateCcw, BookOpen, ClipboardList, MessageSquare, Video, FileSignature,
  Sparkles, User, Loader2, Link2
} from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { format, formatDistanceToNow } from 'date-fns';

// BirdenMedia Color Theme
const theme = {
  bg: '#0B0F1A',
  cardBg: '#111827',
  cardBgHover: '#1a2234',
  primary: '#2563EB',
  secondary: '#FBBF24',
  border: '#1F2937',
  borderLight: '#374151',
  text: '#F9FAFB',
  textMuted: '#9CA3AF',
  textDim: '#6B7280',
  success: '#10B981',
  warning: '#F59E0B',
  danger: '#EF4444',
};

// Relationship/Tier Configs
const relationshipConfig = {
  birdenmedia_client: { label: 'BirdenMedia Client', color: theme.primary, bgColor: 'bg-blue-500/10', textColor: 'text-blue-400' },
  portal_only: { label: 'Portal Only', color: theme.textMuted, bgColor: 'bg-gray-500/10', textColor: 'text-gray-400' },
  bm_assist: { label: 'BM-Assist', color: theme.secondary, bgColor: 'bg-amber-500/10', textColor: 'text-amber-400' },
};

const tierConfig = {
  starter: { label: 'Starter', color: theme.textMuted },
  growth: { label: 'Growth', color: theme.primary },
  partner: { label: 'Partner', color: theme.secondary },
  portal_free: { label: 'Portal Free', color: theme.textMuted },
  portal_pro: { label: 'Portal Pro', color: theme.primary },
  portal_enterprise: { label: 'Portal Enterprise', color: theme.secondary },
};

// Modern Login Component
const AdminLogin = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const res = await adminApi.login(username, password);
      if (res.data.success) {
        localStorage.setItem('admin_token', res.data.token);
        onLogin(res.data.token);
        toast.success('Welcome back');
      }
    } catch (error) {
      toast.error('Invalid credentials');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: theme.bg }}>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          {/* BirdenMedia Logo */}
          <div className="w-24 h-24 mx-auto mb-4">
            <img 
              src="/assets/birdenmedia-logo.png" 
              alt="BirdenMedia" 
              className="w-full h-full object-contain"
            />
          </div>
          <h1 className="text-2xl font-bold" style={{ color: theme.text }}>Admin Portal</h1>
          <p className="mt-2" style={{ color: theme.textMuted }}>Platform Management</p>
        </div>
        
        <form onSubmit={handleSubmit} className="rounded-xl p-8 border" style={{ background: theme.cardBg, borderColor: theme.border }}>
          <div className="space-y-5">
            <div>
              <Label style={{ color: theme.textMuted }}>Username</Label>
              <Input
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="mt-2 h-11 border"
                style={{ background: theme.bg, borderColor: theme.border, color: theme.text }}
                placeholder="Enter username"
                required
              />
            </div>
            <div>
              <Label style={{ color: theme.textMuted }}>Password</Label>
              <div className="relative mt-2">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-11 border pr-12"
                  style={{ background: theme.bg, borderColor: theme.border, color: theme.text }}
                  placeholder="Enter password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 transition-colors"
                  style={{ color: theme.textMuted }}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
          </div>
          
          <Button 
            type="submit" 
            className="w-full mt-8 h-11 font-semibold"
            style={{ background: theme.primary }}
            disabled={isLoading}
          >
            {isLoading ? <RefreshCw className="w-5 h-5 animate-spin" /> : 'Sign In'}
          </Button>
        </form>
      </div>
    </div>
  );
};

// Platform Stat Card - Mobile Responsive
const StatCard = ({ label, value, subValue, icon: Icon }) => (
  <div className="rounded-xl p-4 sm:p-5 border" style={{ background: theme.cardBg, borderColor: theme.border }}>
    <div className="flex items-start justify-between">
      <div className="min-w-0 flex-1">
        <p className="text-xs sm:text-sm font-medium truncate" style={{ color: theme.textMuted }}>{label}</p>
        <p className="text-xl sm:text-2xl md:text-3xl font-bold mt-1 truncate" style={{ color: theme.text }}>{value}</p>
        {subValue && <p className="text-xs sm:text-sm mt-1 truncate" style={{ color: theme.textDim }}>{subValue}</p>}
      </div>
      <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center flex-shrink-0 ml-2" style={{ background: `${theme.primary}20` }}>
        <Icon className="w-4 h-4 sm:w-5 sm:h-5" style={{ color: theme.primary }} />
      </div>
    </div>
  </div>
);

// Organization Card - Mobile Responsive
const OrgCard = ({ org, onView, onImpersonate, onSuspend, onArchive }) => {
  const relationship = relationshipConfig[org.relationship_type] || relationshipConfig.portal_only;
  
  // New tier mapping for ContractorOS
  const planConfig = {
    basic: { label: 'Portal Basic', color: theme.textMuted },
    pro: { label: 'Portal Pro', color: theme.primary },
    starter: { label: 'Portal Basic', color: theme.textMuted }, // Legacy mapping
    professional: { label: 'Portal Pro', color: theme.primary }, // Legacy mapping
  };
  const plan = planConfig[org.service_tier] || planConfig.basic;
  
  // Determine billing status
  const getBillingStatus = () => {
    if (org.relationship_type === 'birdenmedia_client') {
      return { label: 'Included', color: theme.success, bg: 'bg-emerald-500/10' };
    }
    if (org.discount_type && org.discount_type !== 'none') {
      return { label: 'Discounted', color: theme.secondary, bg: 'bg-amber-500/10' };
    }
    return { label: 'Full Price', color: theme.textMuted, bg: '' };
  };
  const billing = getBillingStatus();
  
  const statusColors = {
    active: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', icon: CheckCircle },
    paused: { bg: 'bg-amber-500/10', text: 'text-amber-400', icon: PauseCircle },
    cancelled: { bg: 'bg-red-500/10', text: 'text-red-400', icon: XCircle },
  };
  const status = statusColors[org.status] || statusColors.active;
  const StatusIcon = status.icon;

  return (
    <div 
      className="rounded-xl p-4 sm:p-5 border transition-all hover:border-blue-500/30 cursor-pointer"
      style={{ background: theme.cardBg, borderColor: theme.border }}
      onClick={() => onView(org)}
    >
      <div className="flex items-start justify-between mb-3 gap-2">
        <div className="flex items-start gap-2 sm:gap-3 min-w-0 flex-1">
          <div className="w-9 h-9 sm:w-11 sm:h-11 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${theme.primary}15` }}>
            <Building2 className="w-4 h-4 sm:w-5 sm:h-5" style={{ color: theme.primary }} />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="font-semibold text-sm sm:text-base truncate" style={{ color: theme.text }}>{org.company_name}</h3>
            <p className="text-xs sm:text-sm truncate" style={{ color: theme.textMuted }}>{org.owner_email || org.email}</p>
          </div>
        </div>
        <div className={`flex items-center gap-1 px-1.5 sm:px-2 py-0.5 rounded-full flex-shrink-0 ${status.bg}`}>
          <StatusIcon className={`w-3 h-3 ${status.text}`} />
          <span className={`text-xs font-medium capitalize ${status.text} hidden sm:inline`}>{org.status || 'Active'}</span>
        </div>
      </div>
      
      {/* Relationship + Plan + Billing Status */}
      <div className="flex items-center gap-1.5 sm:gap-2 mb-3 flex-wrap">
        <Badge className={`text-xs ${relationship.bgColor} ${relationship.textColor} border-0 px-1.5 sm:px-2`}>
          {relationship.label}
        </Badge>
        <Badge variant="outline" className="text-xs px-1.5 sm:px-2" style={{ borderColor: theme.border, color: plan.color }}>
          {plan.label}
        </Badge>
        {billing.label !== 'Full Price' && (
          <Badge className={`text-xs ${billing.bg} border-0 px-1.5 sm:px-2`} style={{ color: billing.color }}>
            {billing.label}
          </Badge>
        )}
      </div>
      
      <div className="flex items-center justify-between text-xs sm:text-sm" style={{ color: theme.textDim }}>
        <div className="flex items-center gap-2 sm:gap-4">
          <span className="flex items-center gap-1">
            <Users className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
            {org.user_count || 0}
          </span>
          <span className="flex items-center gap-1">
            <Calendar className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
            <span className="hidden sm:inline">{org.created_at ? format(new Date(org.created_at), 'MMM d, yyyy') : 'N/A'}</span>
            <span className="sm:hidden">{org.created_at ? format(new Date(org.created_at), 'M/d/yy') : 'N/A'}</span>
          </span>
        </div>
        {org.relationship_type === 'birdenmedia_client' ? (
          <span className="text-xs" style={{ color: theme.success }}>Included</span>
        ) : org.birdenmedia_monthly_fee > 0 && (
          <span className="text-xs" style={{ color: theme.success }}>${Number(org.birdenmedia_monthly_fee).toFixed(0)}/mo</span>
        )}
      </div>
      
      {/* Action Buttons - Stack vertically on mobile */}
      <div className="flex items-center gap-1 sm:gap-2 mt-3 sm:mt-4 pt-3 sm:pt-4 border-t" style={{ borderColor: theme.border }}>
        <Button 
          size="sm" 
          variant="ghost" 
          onClick={(e) => { e.stopPropagation(); onView(org); }}
          className="flex-1 h-8 text-xs sm:text-sm px-2 sm:px-3"
          style={{ color: theme.textMuted }}
        >
          <Eye className="w-3.5 h-3.5 sm:mr-1.5" />
          <span className="hidden sm:inline">Details</span>
        </Button>
        <Button 
          size="sm" 
          variant="ghost"
          onClick={(e) => { e.stopPropagation(); onImpersonate(org); }}
          className="flex-1 h-8 text-xs sm:text-sm px-2 sm:px-3"
          style={{ color: theme.primary }}
        >
          <LogIn className="w-3.5 h-3.5 sm:mr-1.5" />
          <span className="hidden sm:inline">Login As</span>
        </Button>
        <Button 
          size="sm" 
          variant="ghost"
          onClick={(e) => { e.stopPropagation(); onSuspend(org); }}
          className="h-8 px-2"
          style={{ color: org.status === 'paused' ? theme.success : theme.danger }}
        >
          {org.status === 'paused' ? <CheckCircle className="w-3.5 h-3.5" /> : <Ban className="w-3.5 h-3.5" />}
        </Button>
        {onArchive && (
          <Button 
            size="sm" 
            variant="ghost"
            onClick={(e) => { e.stopPropagation(); onArchive(org); }}
            className="h-8 px-2"
            style={{ color: theme.secondary }}
            title="Archive"
          >
            <Archive className="w-3.5 h-3.5" />
          </Button>
        )}
      </div>
    </div>
  );
};

// Platform Settings Panel (BirdenMedia payment settings)
const InquiriesPanel = ({ token, theme }) => {
  const [inquiries, setInquiries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [convertingId, setConvertingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const loadInquiries = async () => {
    setIsLoading(true);
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/inquiries?admin_token=${token}${filterStatus !== 'all' ? `&status=${filterStatus}` : ''}`;
      const res = await fetch(url);
      const data = await res.json();
      setInquiries(data.inquiries || []);
    } catch (err) {
      console.error('Failed to load inquiries:', err);
      toast.error('Could not load inquiries');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { loadInquiries(); }, [filterStatus]); // eslint-disable-line

  const updateStatus = async (id, status) => {
    try {
      await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/inquiries/${id}?admin_token=${token}`,
        { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status }) }
      );
      toast.success(`Marked as ${status}`);
      loadInquiries();
    } catch { toast.error('Update failed'); }
  };

  const deleteInquiry = async (id) => {
    if (!window.confirm('Delete this inquiry permanently?')) return;
    try {
      await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/inquiries/${id}?admin_token=${token}`,
        { method: 'DELETE' }
      );
      toast.success('Inquiry deleted');
      loadInquiries();
    } catch { toast.error('Delete failed'); }
  };

  const convertToClient = async (inquiry) => {
    if (!window.confirm(`Convert "${inquiry.company || inquiry.name}" into a paying client? They'll receive a welcome email with a login link.`)) return;
    setConvertingId(inquiry.id);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/inquiries/${inquiry.id}/convert?admin_token=${token}`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ trial_days: 30 }) }
      );
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Convert failed');
      }
      const data = await res.json();
      toast.success(data.message || 'Converted to client!');
      loadInquiries();
    } catch (e) {
      toast.error(e.message || 'Convert failed');
    } finally {
      setConvertingId(null);
    }
  };

  const statusColors = {
    new: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
    contacted: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
    converted: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
    archived: 'bg-gray-500/15 text-gray-400 border-gray-500/30',
  };
  
  const counts = inquiries.reduce((acc, i) => { acc[i.status || 'new'] = (acc[i.status || 'new'] || 0) + 1; return acc; }, {});

  return (
    <div className="space-y-4" data-testid="admin-inquiries-panel">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold" style={{ color: theme.text }}>Inquiries</h2>
          <p className="text-sm" style={{ color: theme.textMuted }}>
            Submissions from your landing page contact form
          </p>
        </div>
        <Button
          onClick={loadInquiries}
          size="sm"
          className="self-start text-xs h-8"
          style={{ background: theme.bg, color: theme.text, border: `1px solid ${theme.border}` }}
          data-testid="refresh-inquiries-btn"
        >
          <Activity className="w-4 h-4 mr-1.5" /> Refresh
        </Button>
      </div>

      {/* Filter chips */}
      <div className="flex flex-wrap gap-2">
        {['all', 'new', 'contacted', 'converted', 'archived'].map(s => (
          <button
            key={s}
            onClick={() => setFilterStatus(s)}
            className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
              filterStatus === s
                ? 'bg-primary text-white border-primary'
                : 'border-gray-300 hover:bg-gray-100'
            }`}
            style={filterStatus === s ? {} : { color: theme.text, borderColor: theme.border }}
            data-testid={`inquiry-filter-${s}`}
          >
            {s.charAt(0).toUpperCase() + s.slice(1)}
            {s !== 'all' && counts[s] ? ` · ${counts[s]}` : ''}
          </button>
        ))}
      </div>

      {/* List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12 text-sm" style={{ color: theme.textMuted }}>
          Loading inquiries…
        </div>
      ) : inquiries.length === 0 ? (
        <div className="text-center py-16 rounded-xl border-2 border-dashed" style={{ borderColor: theme.border }}>
          <Mail className="w-10 h-10 mx-auto mb-3 opacity-30" style={{ color: theme.text }} />
          <p className="font-medium" style={{ color: theme.text }}>No inquiries yet</p>
          <p className="text-sm mt-1" style={{ color: theme.textMuted }}>
            Submissions from your landing page contact form will show up here.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {inquiries.map(inq => {
            const isExpanded = expandedId === inq.id;
            const status = inq.status || 'new';
            return (
              <div key={inq.id} className="rounded-xl border p-4 transition-all" style={{ background: theme.cardBg, borderColor: theme.border }}>
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                  <button
                    onClick={() => setExpandedId(isExpanded ? null : inq.id)}
                    className="flex-1 text-left min-w-0"
                    data-testid={`inquiry-${inq.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center font-semibold text-sm" style={{ background: theme.bg, color: theme.text }}>
                        {(inq.name || '?').charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-semibold truncate" style={{ color: theme.text }}>{inq.name}</p>
                          <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full border ${statusColors[status] || statusColors.new}`}>
                            {status}
                          </span>
                          {inq.niche && inq.niche !== 'Not specified' && (
                            <span className="text-[10px] px-2 py-0.5 rounded-full border" style={{ borderColor: theme.border, color: theme.textMuted }}>
                              {inq.niche}
                            </span>
                          )}
                        </div>
                        <p className="text-sm truncate" style={{ color: theme.textMuted }}>
                          <a href={`mailto:${inq.email}`} className="hover:underline">{inq.email}</a>
                          {inq.company ? ` · ${inq.company}` : ''}
                          {inq.phone ? ` · ${inq.phone}` : ''}
                        </p>
                        <p className="text-xs mt-0.5" style={{ color: theme.textMuted }}>
                          {inq.created_at ? new Date(inq.created_at).toLocaleString() : ''}
                          {inq.employees_count ? ` · ${inq.employees_count} on team` : ''}
                        </p>
                      </div>
                    </div>
                  </button>

                  <div className="flex flex-wrap gap-2 sm:flex-col sm:items-end">
                    {status !== 'converted' && (
                      <Button
                        size="sm"
                        onClick={() => convertToClient(inq)}
                        disabled={convertingId === inq.id}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-8"
                        data-testid={`convert-inquiry-${inq.id}`}
                      >
                        {convertingId === inq.id ? 'Converting…' : '→ Convert to Client'}
                      </Button>
                    )}
                    {status === 'new' && (
                      <Button
                        size="sm"
                        onClick={() => updateStatus(inq.id, 'contacted')}
                        className="text-xs h-8"
                        style={{ background: theme.bg, color: theme.text, border: `1px solid ${theme.border}` }}
                      >
                        Mark Contacted
                      </Button>
                    )}
                    {status !== 'archived' && status !== 'converted' && (
                      <Button
                        size="sm"
                        onClick={() => updateStatus(inq.id, 'archived')}
                        className="text-xs h-8"
                        style={{ background: 'transparent', color: theme.textMuted }}
                      >
                        Archive
                      </Button>
                    )}
                    <button
                      onClick={() => deleteInquiry(inq.id)}
                      className="text-xs text-red-500 hover:text-red-700"
                      title="Delete permanently"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                {isExpanded && inq.message && (
                  <div className="mt-3 pt-3 border-t" style={{ borderColor: theme.border }}>
                    <p className="text-xs uppercase tracking-wide mb-1" style={{ color: theme.textMuted }}>Their message</p>
                    <p className="text-sm whitespace-pre-wrap" style={{ color: theme.text }}>{inq.message}</p>
                  </div>
                )}

                {inq.converted_to_org_id && (
                  <div className="mt-2 text-xs flex items-center gap-1.5" style={{ color: theme.textMuted }}>
                    <CheckCircle className="w-3 h-3 text-emerald-500" />
                    Converted to org {inq.converted_to_org_id.slice(-12)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};


const PlatformSettingsPanel = ({ token }) => {
  const [settings, setSettings] = useState({
    company_name: 'BirdenMedia',
    payment_method: 'stripe',
    stripe_account_id: '',
    cashapp_tag: '',
    billing_email: 'billing@birdenmedia.co'
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setIsLoading(true);
    try {
      const response = await adminApi.getPlatformSettings(token);
      if (response.data) {
        setSettings(response.data);
      }
    } catch (error) {
      console.error('Failed to load settings:', error);
    }
    setIsLoading(false);
  };

  const saveSettings = async () => {
    setIsSaving(true);
    try {
      await adminApi.updatePlatformSettings(token, settings);
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    }
    setIsSaving(false);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 animate-spin" style={{ color: theme.primary }} />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-6">
      {/* Payment Settings */}
      <div className="rounded-xl p-6 border" style={{ background: theme.cardBg, borderColor: theme.border }}>
        <h3 className="font-semibold mb-6 flex items-center gap-2" style={{ color: theme.text }}>
          <DollarSign className="w-5 h-5" style={{ color: theme.primary }} />
          Payment Settings
        </h3>
        
        <div className="space-y-4">
          <div>
            <Label className="text-sm" style={{ color: theme.textMuted }}>Payment Method</Label>
            <Select value={settings.payment_method} onValueChange={(v) => setSettings({...settings, payment_method: v})}>
              <SelectTrigger className="mt-1.5 h-10 border" style={{ background: theme.bg, borderColor: theme.border, color: theme.text }}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent style={{ background: theme.cardBg, borderColor: theme.border }}>
                <SelectItem value="stripe">Stripe</SelectItem>
                <SelectItem value="cashapp">CashApp</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {settings.payment_method === 'stripe' && (
            <div>
              <Label className="text-sm" style={{ color: theme.textMuted }}>Stripe Account ID</Label>
              <Input
                value={settings.stripe_account_id || ''}
                onChange={(e) => setSettings({...settings, stripe_account_id: e.target.value})}
                placeholder="acct_..."
                className="mt-1.5 h-10 border"
                style={{ background: theme.bg, borderColor: theme.border, color: theme.text }}
              />
            </div>
          )}
          
          {settings.payment_method === 'cashapp' && (
            <div>
              <Label className="text-sm" style={{ color: theme.textMuted }}>CashApp Tag</Label>
              <Input
                value={settings.cashapp_tag || ''}
                onChange={(e) => setSettings({...settings, cashapp_tag: e.target.value})}
                placeholder="$YourCashTag"
                className="mt-1.5 h-10 border"
                style={{ background: theme.bg, borderColor: theme.border, color: theme.text }}
              />
            </div>
          )}
          
          <div>
            <Label className="text-sm" style={{ color: theme.textMuted }}>Billing Email</Label>
            <Input
              type="email"
              value={settings.billing_email || ''}
              onChange={(e) => setSettings({...settings, billing_email: e.target.value})}
              placeholder="billing@birdenmedia.co"
              className="mt-1.5 h-10 border"
              style={{ background: theme.bg, borderColor: theme.border, color: theme.text }}
            />
          </div>
          
          <Button 
            onClick={saveSettings} 
            disabled={isSaving}
            className="w-full mt-4"
            style={{ background: theme.primary }}
          >
            {isSaving ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
            Save Settings
          </Button>
        </div>
      </div>
      
      {/* Billing Info */}
      <div className="rounded-xl p-6 border" style={{ background: theme.cardBg, borderColor: theme.border }}>
        <h3 className="font-semibold mb-6 flex items-center gap-2" style={{ color: theme.text }}>
          <Receipt className="w-5 h-5" style={{ color: theme.secondary }} />
          Quick Stats
        </h3>
        
        <div className="space-y-4">
          <div className="p-4 rounded-lg" style={{ background: theme.bg }}>
            <p className="text-sm" style={{ color: theme.textMuted }}>Active Payment Method</p>
            <p className="text-xl font-bold mt-1" style={{ color: theme.text }}>
              {settings.payment_method === 'stripe' ? 'Stripe' : 'CashApp'}
            </p>
          </div>
          
          <div className="p-4 rounded-lg" style={{ background: `${theme.primary}10` }}>
            <p className="text-sm" style={{ color: theme.textMuted }}>Note</p>
            <p className="text-sm mt-1" style={{ color: theme.text }}>
              When you create invoices for your clients, they'll see these payment details in the invoice email.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Organization Invoice Section (BirdenMedia invoices to this org)
const OrgInvoiceSection = ({ orgId, orgName, monthlyRate, token }) => {
  const [invoices, setInvoices] = useState([]);
  const [billing, setBilling] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const { showConfirm: showInvoiceConfirm, ConfirmDialogComponent: InvoiceConfirmDialog } = useConfirmDialog();

  const loadInvoices = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await adminApi.getOrgBillingSummary(orgId, token);
      setBilling(res.data);
      setInvoices(res.data.invoices || []);
    } catch (error) {
      console.error('Failed to load invoices:', error);
    }
    setIsLoading(false);
  }, [orgId, token]);

  useEffect(() => { loadInvoices(); }, [loadInvoices]);

  const handleCreateInvoice = async (invoiceData) => {
    try {
      await adminApi.createAdminInvoice(token, { ...invoiceData, org_id: orgId });
      toast.success('Invoice created');
      setShowCreateModal(false);
      loadInvoices();
    } catch (error) {
      toast.error('Failed to create invoice');
    }
  };

  const handleUpdateInvoice = async (invoiceId, updates) => {
    try {
      await adminApi.updateAdminInvoice(invoiceId, token, updates);
      toast.success('Invoice updated');
      setEditingInvoice(null);
      loadInvoices();
    } catch (error) {
      toast.error('Failed to update invoice');
    }
  };

  const handleSendInvoice = async (invoiceId) => {
    try {
      const res = await adminApi.sendAdminInvoice(invoiceId, token);
      if (res.data.status === 'success') {
        toast.success('Invoice sent via email');
      } else {
        toast.warning(res.data.message || 'Invoice marked as sent');
      }
      loadInvoices();
    } catch (error) {
      toast.error('Failed to send invoice');
    }
  };

  const handleMarkPaid = async (invoice) => {
    const method = prompt('Payment method? (stripe / cashapp / check / bank_transfer)', 'stripe');
    if (!method) return;
    const reference = prompt('Transaction/reference ID (optional):', '');
    try {
      await adminApi.markAdminInvoicePaid(invoice.id, token, {
        paid_amount: invoice.amount,
        payment_method: method,
        payment_reference: reference || undefined
      });
      toast.success('Invoice marked as paid');
      loadInvoices();
    } catch (error) {
      toast.error('Failed to mark as paid');
    }
  };

  const handleCopyPaymentLink = async (invoiceId) => {
    try {
      const res = await adminApi.getAdminInvoicePaymentLink(invoiceId, token);
      await navigator.clipboard.writeText(res.data.payment_link);
      toast.success('Payment link copied to clipboard');
    } catch (error) {
      toast.error('Failed to get payment link');
    }
  };

  const handleDeleteInvoice = async (invoiceId) => {
    const confirmed = await showInvoiceConfirm({
      title: 'Delete Invoice?',
      description: 'This action cannot be undone. The invoice will be permanently removed.',
      variant: 'danger',
      confirmText: 'Delete',
      destructive: true
    });
    if (!confirmed) return;
    try {
      await adminApi.deleteAdminInvoice(invoiceId, token);
      toast.success('Invoice deleted');
      loadInvoices();
    } catch (error) {
      toast.error('Failed to delete invoice');
    }
  };

  const statusColors = {
    draft: { bg: 'bg-gray-500/20', text: 'text-gray-400' },
    sent: { bg: 'bg-blue-500/20', text: 'text-blue-400' },
    paid: { bg: 'bg-green-500/20', text: 'text-green-400' },
    overdue: { bg: 'bg-red-500/20', text: 'text-red-400' },
    cancelled: { bg: 'bg-gray-500/20', text: 'text-gray-500' }
  };

  return (
    <div className="rounded-xl p-6 border mt-6" style={{ background: theme.cardBg, borderColor: theme.border }}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="font-semibold flex items-center gap-2" style={{ color: theme.text }}>
            <Receipt className="w-5 h-5" style={{ color: theme.primary }} />
            Your Invoices to {orgName}
          </h3>
          {billing && (
            <div className="flex items-center gap-4 mt-2 text-sm">
              <span style={{ color: theme.textMuted }}>
                Total Billed: <span className="font-semibold" style={{ color: theme.text }}>${billing.total_billed?.toFixed(2)}</span>
              </span>
              <span style={{ color: theme.textMuted }}>
                Paid: <span className="font-semibold" style={{ color: theme.success }}>${billing.total_paid?.toFixed(2)}</span>
              </span>
              {billing.outstanding_balance > 0 && (
                <span style={{ color: theme.warning }}>
                  Outstanding: <span className="font-bold">${billing.outstanding_balance?.toFixed(2)}</span>
                </span>
              )}
            </div>
          )}
        </div>
        <Button onClick={() => setShowCreateModal(true)} size="sm" style={{ background: theme.primary }}>
          <Plus className="w-4 h-4 mr-1" /> New Invoice
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8">
          <RefreshCw className="w-6 h-6 animate-spin" style={{ color: theme.primary }} />
        </div>
      ) : invoices.length === 0 ? (
        <div className="text-center py-8 rounded-lg" style={{ background: theme.bg }}>
          <Receipt className="w-10 h-10 mx-auto mb-2" style={{ color: theme.textDim }} />
          <p style={{ color: theme.textMuted }}>No invoices yet</p>
          <p className="text-sm mt-1" style={{ color: theme.textDim }}>Create your first invoice for this client</p>
        </div>
      ) : (
        <div className="space-y-3">
          {invoices.map((invoice) => (
            <div key={invoice.id} className="flex items-center justify-between p-4 rounded-lg" style={{ background: theme.bg }}>
              <div className="flex items-center gap-4">
                <div>
                  <p className="font-medium" style={{ color: theme.text }}>
                    {invoice.description}
                  </p>
                  <p className="text-sm" style={{ color: theme.textMuted }}>
                    {invoice.period_start} to {invoice.period_end} • Due: {invoice.due_date}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-lg font-bold" style={{ color: theme.text }}>${invoice.amount?.toFixed(2)}</span>
                <Badge className={`${statusColors[invoice.status]?.bg} ${statusColors[invoice.status]?.text}`}>
                  {invoice.status}
                </Badge>
                <div className="flex items-center gap-1">
                  {invoice.status !== 'paid' && invoice.status !== 'cancelled' && (
                    <Button size="sm" variant="ghost" onClick={() => handleCopyPaymentLink(invoice.id)} title="Copy Payment Link">
                      <Link2 className="w-4 h-4" style={{ color: theme.primary }} />
                    </Button>
                  )}
                  {invoice.status === 'draft' && (
                    <Button size="sm" variant="ghost" onClick={() => handleSendInvoice(invoice.id)} title="Send Invoice">
                      <Send className="w-4 h-4" style={{ color: theme.primary }} />
                    </Button>
                  )}
                  {(invoice.status === 'sent' || invoice.status === 'overdue') && (
                    <Button size="sm" variant="ghost" onClick={() => handleMarkPaid(invoice)} title="Mark as Paid">
                      <CheckCircle className="w-4 h-4" style={{ color: theme.success }} />
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => setEditingInvoice(invoice)} title="Edit">
                    <Pencil className="w-4 h-4" style={{ color: theme.textMuted }} />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => handleDeleteInvoice(invoice.id)} title="Delete">
                    <Trash2 className="w-4 h-4" style={{ color: theme.danger }} />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Invoice Modal */}
      {showCreateModal && (
        <InvoiceModal
          orgName={orgName}
          defaultAmount={monthlyRate}
          onSave={handleCreateInvoice}
          onClose={() => setShowCreateModal(false)}
        />
      )}

      {/* Edit Invoice Modal */}
      {editingInvoice && (
        <InvoiceModal
          invoice={editingInvoice}
          orgName={orgName}
          onSave={(data) => handleUpdateInvoice(editingInvoice.id, data)}
          onClose={() => setEditingInvoice(null)}
        />
      )}
      {InvoiceConfirmDialog}
    </div>
  );
};

// Invoice Create/Edit Modal
const InvoiceModal = ({ invoice, orgName, defaultAmount = 0, onSave, onClose }) => {
  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const lastOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  const dueDate = new Date(today.getFullYear(), today.getMonth() + 1, 15);

  const [form, setForm] = useState({
    amount: invoice?.amount || defaultAmount,
    description: invoice?.description || 'Monthly subscription - Portal',
    period_start: invoice?.period_start || firstOfMonth.toISOString().split('T')[0],
    period_end: invoice?.period_end || lastOfMonth.toISOString().split('T')[0],
    due_date: invoice?.due_date || dueDate.toISOString().split('T')[0],
    status: invoice?.status || 'draft',
    notes: invoice?.notes || ''
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4" style={{ background: `${theme.bg}ee` }}>
      <div className="w-full max-w-md max-h-[90vh] overflow-y-auto rounded-xl border p-4 sm:p-6" style={{ background: theme.cardBg, borderColor: theme.border }}>
        <div className="flex items-center justify-between mb-4 sm:mb-6">
          <h3 className="text-base sm:text-lg font-semibold truncate pr-2" style={{ color: theme.text }}>
            {invoice ? 'Edit Invoice' : 'New Invoice'} {orgName ? `for ${orgName}` : ''}
          </h3>
          <Button variant="ghost" size="sm" onClick={onClose} className="flex-shrink-0">
            <X className="w-4 h-4" style={{ color: theme.textMuted }} />
          </Button>
        </div>

        <div className="space-y-3 sm:space-y-4">
          <div>
            <Label className="text-xs sm:text-sm" style={{ color: theme.textMuted }}>Amount ($)</Label>
            <Input
              type="text"
              inputMode="decimal"
              value={form.amount === 0 ? '' : form.amount}
              onChange={(e) => {
                const val = e.target.value.replace(/[^0-9.]/g, '');
                setForm({...form, amount: val === '' ? 0 : parseFloat(val) || 0});
              }}
              placeholder="0.00"
              className="mt-1 h-9 sm:h-10 border text-sm"
              style={{ background: theme.bg, borderColor: theme.border, color: theme.text }}
            />
          </div>
          <div>
            <Label className="text-xs sm:text-sm" style={{ color: theme.textMuted }}>Description</Label>
            <Input
              value={form.description}
              onChange={(e) => setForm({...form, description: e.target.value})}
              className="mt-1 h-9 sm:h-10 border text-sm"
              style={{ background: theme.bg, borderColor: theme.border, color: theme.text }}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs sm:text-sm" style={{ color: theme.textMuted }}>Period Start</Label>
              <Input
                type="date"
                value={form.period_start}
                onChange={(e) => setForm({...form, period_start: e.target.value})}
                className="mt-1 h-9 sm:h-10 border text-sm"
                style={{ background: theme.bg, borderColor: theme.border, color: theme.text }}
              />
            </div>
            <div>
              <Label className="text-xs sm:text-sm" style={{ color: theme.textMuted }}>Period End</Label>
              <Input
                type="date"
                value={form.period_end}
                onChange={(e) => setForm({...form, period_end: e.target.value})}
                className="mt-1 h-9 sm:h-10 border text-sm"
                style={{ background: theme.bg, borderColor: theme.border, color: theme.text }}
              />
            </div>
          </div>
          <div>
            <Label className="text-xs sm:text-sm" style={{ color: theme.textMuted }}>Due Date</Label>
            <Input
              type="date"
              value={form.due_date}
              onChange={(e) => setForm({...form, due_date: e.target.value})}
              className="mt-1 h-9 sm:h-10 border text-sm"
              style={{ background: theme.bg, borderColor: theme.border, color: theme.text }}
            />
          </div>
          {invoice && (
            <div>
              <Label className="text-xs sm:text-sm" style={{ color: theme.textMuted }}>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({...form, status: v})}>
                <SelectTrigger className="mt-1 h-9 sm:h-10 border text-sm" style={{ background: theme.bg, borderColor: theme.border, color: theme.text }}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent style={{ background: theme.cardBg, borderColor: theme.border }}>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="sent">Sent</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="text-xs sm:text-sm" style={{ color: theme.textMuted }}>Notes (optional)</Label>
            <Input
              value={form.notes}
              onChange={(e) => setForm({...form, notes: e.target.value})}
              placeholder="e.g., Comped this month"
              className="mt-1 h-9 sm:h-10 border text-sm"
              style={{ background: theme.bg, borderColor: theme.border, color: theme.text }}
            />
          </div>
        </div>

        <div className="flex gap-2 sm:gap-3 mt-4 sm:mt-6">
          <Button variant="outline" onClick={onClose} className="flex-1 h-9 sm:h-10 text-sm" style={{ borderColor: theme.border, color: theme.text }}>
            Cancel
          </Button>
          <Button onClick={() => onSave(form)} className="flex-1 h-9 sm:h-10 text-sm" style={{ background: theme.primary }}>
            {invoice ? 'Update' : 'Create'}
          </Button>
        </div>
      </div>
    </div>
  );
};

// Create Organization Modal
const CreateOrgModal = ({ open, onClose, onCreated, token }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [createdResult, setCreatedResult] = useState(null);
  const [formData, setFormData] = useState({
    company_name: '', email: '', phone: '', address: '', industry: '',
    owner_name: '', owner_email: '',
    relationship_type: 'portal_only', service_tier: 'basic',
    birdenmedia_monthly_fee: 79,
    discount_type: 'none',
    custom_discount_percent: '',
    custom_discount_name: '',
    // Feature toggles (defaults based on tier)
    feature_estimates: true,
    feature_jobs: true,
    feature_invoices: true,
    feature_calendar: true,
    feature_offline: true,
    feature_crew_management: false,
    feature_automation: false,
    feature_reporting: false,
    // Onboarding options
    send_welcome_email: true,  // Auto-send welcome email with credentials
  });

  // New ContractorOS Pricing Tiers
  const pricingTiers = {
    basic: { 
      name: 'Portal Basic', 
      price: 79, 
      features: '1 user, 50 clients, estimates, jobs, invoices, calendar, offline',
      defaultFeatures: {
        feature_estimates: true,
        feature_jobs: true,
        feature_invoices: true,
        feature_calendar: true,
        feature_offline: true,
        feature_crew_management: false,
        feature_automation: false,
        feature_reporting: false,
      }
    },
    pro: { 
      name: 'Portal Pro', 
      price: 149, 
      features: '5 users, unlimited clients, crew management, automation, reporting',
      defaultFeatures: {
        feature_estimates: true,
        feature_jobs: true,
        feature_invoices: true,
        feature_calendar: true,
        feature_offline: true,
        feature_crew_management: true,
        feature_automation: true,
        feature_reporting: true,
      }
    }
  };

  // Simplified Discount options
  const discountOptions = [
    { value: 'none', label: 'No Discount', percent: 0 },
    { value: 'partnership', label: 'Strategic Partnership', percent: 30 },
    { value: 'military_fr', label: 'Military / First Responder', percent: 20 },
    { value: 'custom', label: 'Custom Discount', percent: 0 }
  ];

  // Calculate final price
  const getDiscountPercent = () => {
    if (formData.discount_type === 'custom') {
      return parseFloat(formData.custom_discount_percent) || 0;
    }
    const discount = discountOptions.find(d => d.value === formData.discount_type);
    return discount ? discount.percent : 0;
  };

  const calculateFinalPrice = () => {
    // BirdenMedia clients get Pro included free
    if (formData.relationship_type === 'birdenmedia_client') {
      return 0;
    }
    const basePrice = pricingTiers[formData.service_tier]?.price || 0;
    const discountPercent = getDiscountPercent();
    const discountAmount = basePrice * (discountPercent / 100);
    return Math.round((basePrice - discountAmount) * 100) / 100;
  };

  const getBillingStatus = () => {
    if (formData.relationship_type === 'birdenmedia_client') {
      return 'Included with BirdenMedia';
    }
    if (getDiscountPercent() > 0) {
      return `Discounted (${getDiscountPercent()}% off)`;
    }
    return 'Full Price';
  };

  // Auto-set Pro plan and features for BirdenMedia clients
  useEffect(() => {
    if (formData.relationship_type === 'birdenmedia_client') {
      setFormData(prev => ({ 
        ...prev, 
        service_tier: 'pro',
        discount_type: 'none',
        ...pricingTiers.pro.defaultFeatures
      }));
    }
  }, [formData.relationship_type]);

  // Update features when tier changes
  useEffect(() => {
    if (formData.relationship_type !== 'birdenmedia_client') {
      const tierFeatures = pricingTiers[formData.service_tier]?.defaultFeatures || {};
      setFormData(prev => ({ ...prev, ...tierFeatures }));
    }
  }, [formData.service_tier]);

  // Update monthly fee when tier or discount changes
  useEffect(() => {
    setFormData(prev => ({ ...prev, birdenmedia_monthly_fee: calculateFinalPrice() }));
  }, [formData.service_tier, formData.discount_type, formData.custom_discount_percent, formData.relationship_type]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      // Map to existing API structure (password will be auto-generated)
      // Clean up the payload to match backend expectations
      const payload = {
        ...formData,
        plan_name: formData.service_tier,
        // Ensure numeric fields are proper numbers or null
        custom_discount_percent: formData.custom_discount_percent 
          ? parseFloat(formData.custom_discount_percent) 
          : null,
        custom_discount_name: formData.custom_discount_name || null,
      };
      const response = await adminApi.createOrganization(payload, token);
      setCreatedResult(response.data); // Store result.data to get the actual response
      toast.success(`Organization "${formData.company_name}" created!`);
      onCreated();
    } catch (error) {
      // Handle Pydantic validation errors (detail is an array) vs simple errors (detail is a string)
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        // Pydantic validation error - extract first error message
        const firstError = detail[0];
        toast.error(firstError?.msg || 'Validation failed');
      } else {
        toast.error(detail || 'Failed to create organization');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendWelcomeEmail = async () => {
    console.log('handleSendWelcomeEmail called, createdResult:', createdResult);
    if (!createdResult?.user_id) {
      console.error('No user_id found in createdResult:', createdResult);
      toast.error('No user found to send email to');
      return;
    }
    setIsLoading(true);
    try {
      console.log('Sending welcome email to user:', createdResult.user_id);
      await adminApi.sendWelcomeEmail(createdResult.user_id, token);
      toast.success('Welcome email sent!');
      onClose();
      setCreatedResult(null);
      setFormData({
        company_name: '', email: '', phone: '', address: '', industry: '',
        owner_name: '', owner_email: '',
        relationship_type: 'portal_only', service_tier: 'basic',
        birdenmedia_monthly_fee: 79,
        discount_type: 'none',
        custom_discount_percent: '',
        custom_discount_name: '',
        feature_estimates: true,
        feature_jobs: true,
        feature_invoices: true,
        feature_calendar: true,
        feature_offline: true,
        feature_crew_management: false,
        feature_automation: false,
        feature_reporting: false,
        send_welcome_email: true,
      });
    } catch (error) {
      console.error('Failed to send welcome email:', error);
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        toast.error(detail[0]?.msg || 'Failed to send welcome email');
      } else {
        toast.error(detail || 'Failed to send welcome email');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setCreatedResult(null);
    setFormData({
      company_name: '', email: '', phone: '', address: '', industry: '',
      owner_name: '', owner_email: '',
      relationship_type: 'portal_only', service_tier: 'basic',
      birdenmedia_monthly_fee: 79,
      discount_type: 'none',
      custom_discount_percent: '',
      custom_discount_name: '',
      feature_estimates: true,
      feature_jobs: true,
      feature_invoices: true,
      feature_calendar: true,
      feature_offline: true,
      feature_crew_management: false,
      feature_automation: false,
      feature_reporting: false,
      send_welcome_email: true,
    });
    onClose();
  };

  const tiers = formData.relationship_type === 'birdenmedia_client' 
    ? [{ value: 'starter', label: 'Starter' }, { value: 'growth', label: 'Growth' }, { value: 'partner', label: 'Partner' }]
    : [{ value: 'portal_free', label: 'Portal Free' }, { value: 'portal_pro', label: 'Portal Pro' }, { value: 'portal_enterprise', label: 'Portal Enterprise' }];

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg w-[95vw] max-h-[90vh] overflow-y-auto border p-4 sm:p-6" style={{ background: theme.cardBg, borderColor: theme.border, color: theme.text }}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
            <Plus className="w-4 h-4 sm:w-5 sm:h-5" style={{ color: theme.primary }} />
            {createdResult ? 'Organization Created!' : 'Add Organization'}
          </DialogTitle>
        </DialogHeader>
        
        {createdResult ? (
          // Success view with credentials
          <div className="space-y-3 sm:space-y-4 mt-3 sm:mt-4">
            <div className="p-3 sm:p-4 rounded-lg border" style={{ background: theme.bg, borderColor: theme.success }}>
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5" style={{ color: theme.success }} />
                <span className="font-medium text-sm sm:text-base" style={{ color: theme.success }}>Organization Created Successfully!</span>
              </div>
              
              <div className="space-y-2 sm:space-y-3">
                <div>
                  <Label className="text-xs sm:text-sm" style={{ color: theme.textMuted }}>Company</Label>
                  <p className="font-medium text-sm sm:text-base" style={{ color: theme.text }}>{formData.company_name}</p>
                </div>
                <div>
                  <Label className="text-xs sm:text-sm" style={{ color: theme.textMuted }}>Owner Email</Label>
                  <p className="font-medium text-sm sm:text-base truncate" style={{ color: theme.text }}>{formData.owner_email}</p>
                </div>
                <div>
                  <Label className="text-xs sm:text-sm" style={{ color: theme.textMuted }}>Temporary Password</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <code className="px-2 sm:px-3 py-1.5 sm:py-2 rounded text-xs sm:text-sm font-mono flex-1 overflow-x-auto" style={{ background: theme.cardBg, color: theme.secondary }}>
                      {createdResult.temp_password}
                    </code>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        navigator.clipboard.writeText(createdResult.temp_password);
                        toast.success('Password copied!');
                      }}
                      className="h-8 px-2 flex-shrink-0"
                      style={{ color: theme.textMuted }}
                    >
                      Copy
                    </Button>
                  </div>
                </div>
              </div>
            </div>
            
            {createdResult.welcome_email_sent ? (
              <div className="p-4 rounded-lg border" style={{ background: '#16a34a20', borderColor: theme.success }}>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" style={{ color: theme.success }} />
                  <p className="text-sm" style={{ color: theme.success }}>
                    <strong>Welcome email sent!</strong> The owner has received their login credentials and instructions.
                  </p>
                </div>
              </div>
            ) : (
              <div className="p-4 rounded-lg border" style={{ background: '#1e3a5f20', borderColor: theme.primary }}>
                <p className="text-sm" style={{ color: theme.textMuted }}>
                  <strong style={{ color: theme.text }}>Next step:</strong> Send a welcome email to the owner with their login credentials and instructions.
                </p>
              </div>
            )}
            
            <DialogFooter className="pt-4 gap-2">
              <Button 
                type="button"
                variant="outline" 
                onClick={handleClose} 
                style={{ borderColor: theme.border, color: theme.textMuted }}
              >
                {createdResult.welcome_email_sent ? 'Done' : 'Close'}
              </Button>
              {!createdResult.welcome_email_sent && (
                <Button 
                  type="button"
                  onClick={handleSendWelcomeEmail} 
                  disabled={isLoading}
                  style={{ background: theme.primary }}
                  data-testid="send-welcome-email-btn"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  {isLoading ? 'Sending...' : 'Send Welcome Email'}
                </Button>
              )}
            </DialogFooter>
          </div>
        ) : (
        <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4 mt-3 sm:mt-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div className="sm:col-span-2">
              <Label className="text-xs sm:text-sm" style={{ color: theme.textMuted }}>Company Name *</Label>
              <Input
                value={formData.company_name}
                onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                className="mt-1.5 h-9 sm:h-10 border text-sm"
                style={{ background: theme.bg, borderColor: theme.border, color: theme.text }}
                required
              />
            </div>
            <div>
              <Label className="text-xs sm:text-sm" style={{ color: theme.textMuted }}>Email *</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="mt-1.5 h-9 sm:h-10 border text-sm"
                style={{ background: theme.bg, borderColor: theme.border, color: theme.text }}
                required
              />
            </div>
            <div>
              <Label className="text-xs sm:text-sm" style={{ color: theme.textMuted }}>Phone</Label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                className="mt-1.5 h-9 sm:h-10 border text-sm"
                style={{ background: theme.bg, borderColor: theme.border, color: theme.text }}
              />
            </div>
          </div>
          
          <div className="pt-2 border-t" style={{ borderColor: theme.border }}>
            <h4 className="text-xs sm:text-sm font-medium mb-2 sm:mb-3" style={{ color: theme.text }}>Owner Account</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <Label className="text-xs sm:text-sm" style={{ color: theme.textMuted }}>Owner Name *</Label>
                <Input
                  value={formData.owner_name}
                  onChange={(e) => setFormData({...formData, owner_name: e.target.value})}
                  className="mt-1.5 h-9 sm:h-10 border text-sm"
                  style={{ background: theme.bg, borderColor: theme.border, color: theme.text }}
                  required
                />
              </div>
              <div>
                <Label className="text-xs sm:text-sm" style={{ color: theme.textMuted }}>Owner Email *</Label>
                <Input
                  type="email"
                  value={formData.owner_email}
                  onChange={(e) => setFormData({...formData, owner_email: e.target.value})}
                  className="mt-1.5 h-9 sm:h-10 border text-sm"
                  style={{ background: theme.bg, borderColor: theme.border, color: theme.text }}
                  required
                />
              </div>
            </div>
          </div>
          
          <div className="pt-2 border-t" style={{ borderColor: theme.border }}>
            <h4 className="text-xs sm:text-sm font-medium mb-2 sm:mb-3" style={{ color: theme.text }}>Service Configuration</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <Label style={{ color: theme.textMuted }}>Relationship Type</Label>
                <Select value={formData.relationship_type} onValueChange={(v) => setFormData({...formData, relationship_type: v})}>
                  <SelectTrigger className="mt-1.5 h-10 border" style={{ background: theme.bg, borderColor: theme.border, color: theme.text }}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent style={{ background: theme.cardBg, borderColor: theme.border }}>
                    <SelectItem value="birdenmedia_client" style={{ color: theme.text }}>BirdenMedia Client</SelectItem>
                    <SelectItem value="portal_only" style={{ color: theme.text }}>Portal Only</SelectItem>
                    <SelectItem value="bm_assist" style={{ color: theme.text }}>BM-Assist</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label style={{ color: theme.textMuted }}>Portal Plan</Label>
                <Select 
                  value={formData.service_tier} 
                  onValueChange={(v) => setFormData({...formData, service_tier: v})}
                  disabled={formData.relationship_type === 'birdenmedia_client'}
                >
                  <SelectTrigger className="mt-1.5 h-10 border" style={{ background: theme.bg, borderColor: theme.border, color: theme.text }}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent style={{ background: theme.cardBg, borderColor: theme.border }}>
                    {Object.entries(pricingTiers).map(([key, tier]) => (
                      <SelectItem key={key} value={key} style={{ color: theme.text }}>
                        {tier.name} - ${tier.price}/mo
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {formData.relationship_type === 'birdenmedia_client' && (
                  <p className="text-xs mt-1" style={{ color: theme.success }}>Pro plan included with BirdenMedia</p>
                )}
              </div>
              
              {/* ContractorOS Pricing Reference */}
              <div className="sm:col-span-2 p-3 sm:p-4 rounded-lg border" style={{ background: theme.bg, borderColor: theme.border }}>
                <div className="flex items-center gap-2 mb-2 sm:mb-3">
                  <DollarSign className="w-4 h-4" style={{ color: theme.secondary }} />
                  <span className="font-medium text-xs sm:text-sm" style={{ color: theme.text }}>ContractorOS Pricing</span>
                </div>
                <div className="grid grid-cols-2 gap-2 sm:gap-4 text-xs sm:text-sm">
                  <div className="p-2 sm:p-3 rounded border" style={{ 
                    background: formData.service_tier === 'basic' ? theme.primary + '15' : 'transparent',
                    borderColor: formData.service_tier === 'basic' ? theme.primary : theme.border 
                  }}>
                    <div className="font-bold text-sm sm:text-lg" style={{ color: theme.text }}>Basic</div>
                    <div className="text-lg sm:text-2xl font-bold my-1" style={{ color: theme.primary }}>$79<span className="text-xs sm:text-sm font-normal">/mo</span></div>
                    <ul className="space-y-0.5 mt-1 text-xs leading-tight" style={{ color: theme.textMuted }}>
                      <li className="truncate">• 1 user, 50 clients</li>
                      <li className="hidden sm:list-item">• Estimates & Jobs</li>
                      <li className="hidden sm:list-item">• Calendar</li>
                    </ul>
                  </div>
                  <div className="p-2 sm:p-3 rounded border" style={{ 
                    background: formData.service_tier === 'pro' ? theme.primary + '15' : 'transparent',
                    borderColor: formData.service_tier === 'pro' ? theme.primary : theme.border 
                  }}>
                    <div className="font-bold text-sm sm:text-lg" style={{ color: theme.text }}>Pro</div>
                    <div className="text-lg sm:text-2xl font-bold my-1" style={{ color: theme.primary }}>$149<span className="text-xs sm:text-sm font-normal">/mo</span></div>
                    <ul className="space-y-0.5 mt-1 text-xs leading-tight" style={{ color: theme.textMuted }}>
                      <li className="truncate">• 5 users, unlimited</li>
                      <li className="hidden sm:list-item">• Crew management</li>
                      <li className="hidden sm:list-item">• Automation</li>
                    </ul>
                  </div>
                </div>
              </div>
              
              {/* Feature Toggles */}
              <div className="col-span-2 p-4 rounded-lg border" style={{ background: theme.bg, borderColor: theme.border }}>
                <div className="flex items-center gap-2 mb-3">
                  <Settings className="w-4 h-4" style={{ color: theme.secondary }} />
                  <span className="font-medium text-sm" style={{ color: theme.text }}>Feature Overrides</span>
                  <span className="text-xs px-2 py-0.5 rounded" style={{ background: theme.primary + '20', color: theme.primary }}>Optional</span>
                </div>
                <div className="grid grid-cols-4 gap-3 text-sm">
                  {[
                    { key: 'feature_crew_management', label: 'Crew Management' },
                    { key: 'feature_automation', label: 'Automation' },
                    { key: 'feature_reporting', label: 'Reporting' },
                  ].map(feature => (
                    <label key={feature.key} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData[feature.key]}
                        onChange={(e) => setFormData({...formData, [feature.key]: e.target.checked})}
                        className="rounded"
                      />
                      <span style={{ color: theme.text }}>{feature.label}</span>
                    </label>
                  ))}
                </div>
                <p className="text-xs mt-2" style={{ color: theme.textMuted }}>
                  Toggle features on/off regardless of plan. Basic features (estimates, jobs, invoices, calendar, offline) are always included.
                </p>
              </div>
              
              {/* Discount Section - Only show for non-BirdenMedia clients */}
              {formData.relationship_type !== 'birdenmedia_client' && (
                <>
                  <div className="sm:col-span-2">
                    <Label className="text-xs sm:text-sm" style={{ color: theme.textMuted }}>Discount</Label>
                    <Select value={formData.discount_type} onValueChange={(v) => setFormData({...formData, discount_type: v, custom_discount_percent: '', custom_discount_name: ''})}>
                      <SelectTrigger className="mt-1.5 h-9 sm:h-10 border text-sm" style={{ background: theme.bg, borderColor: theme.border, color: theme.text }}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent style={{ background: theme.cardBg, borderColor: theme.border }}>
                        {discountOptions.map(opt => (
                          <SelectItem key={opt.value} value={opt.value} style={{ color: theme.text }}>
                            {opt.label} {opt.percent > 0 ? `(${opt.percent}% off)` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    
                    {/* Discount quick reference */}
                    <div className="mt-2 text-xs" style={{ color: theme.textMuted }}>
                      <div className="flex flex-wrap gap-1 sm:gap-2">
                        <span className="px-1.5 sm:px-2 py-0.5 rounded" style={{ background: theme.bg }}>Partnership: 30%</span>
                        <span className="px-1.5 sm:px-2 py-0.5 rounded" style={{ background: theme.bg }}>Military/FR: 20%</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Custom Discount Fields */}
                  {formData.discount_type === 'custom' && (
                    <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                      <div>
                        <Label className="text-xs sm:text-sm" style={{ color: theme.textMuted }}>Discount Name</Label>
                        <Input
                          placeholder="e.g., Referral Bonus"
                          value={formData.custom_discount_name}
                          onChange={(e) => setFormData({...formData, custom_discount_name: e.target.value})}
                          className="mt-1.5 h-9 sm:h-10 border text-sm"
                          style={{ background: theme.bg, borderColor: theme.border, color: theme.text }}
                        />
                      </div>
                      <div>
                        <Label className="text-xs sm:text-sm" style={{ color: theme.textMuted }}>Discount (%)</Label>
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          placeholder="e.g., 15"
                          value={formData.custom_discount_percent}
                          onChange={(e) => setFormData({...formData, custom_discount_percent: e.target.value})}
                          className="mt-1.5 h-9 sm:h-10 border text-sm"
                          style={{ background: theme.bg, borderColor: theme.border, color: theme.text }}
                        />
                      </div>
                    </div>
                  )}
                </>
              )}
              
              {/* Final Monthly Fee / Billing Status */}
              <div className="sm:col-span-2 p-3 sm:p-4 rounded-lg border-2" style={{ 
                background: formData.relationship_type === 'birdenmedia_client' ? theme.success + '15' : theme.primary + '10', 
                borderColor: formData.relationship_type === 'birdenmedia_client' ? theme.success : theme.primary 
              }}>
                <div className="flex justify-between items-center">
                  <div>
                    <Label style={{ color: theme.textMuted }}>Billing Status</Label>
                    <div className="text-sm mt-1 font-medium" style={{ 
                      color: formData.relationship_type === 'birdenmedia_client' ? theme.success : theme.text 
                    }}>
                      {getBillingStatus()}
                    </div>
                  </div>
                  <div className="text-right">
                    {formData.relationship_type === 'birdenmedia_client' ? (
                      <div>
                        <div className="text-sm" style={{ color: theme.textMuted }}>Portal Pro</div>
                        <div className="text-2xl font-bold" style={{ color: theme.success }}>
                          Included
                        </div>
                      </div>
                    ) : (
                      <div>
                        {getDiscountPercent() > 0 && (
                          <div className="text-sm line-through" style={{ color: theme.textMuted }}>
                            ${pricingTiers[formData.service_tier]?.price || 0}/mo
                          </div>
                        )}
                        <div className="text-2xl font-bold" style={{ color: theme.primary }}>
                          ${calculateFinalPrice()}/mo
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Welcome Email Option */}
          <div className="pt-3 border-t" style={{ borderColor: theme.border }}>
            <div className="flex items-start gap-3 p-3 rounded-lg" style={{ background: theme.bg }}>
              <input
                type="checkbox"
                id="send_welcome_email"
                checked={formData.send_welcome_email}
                onChange={(e) => setFormData({...formData, send_welcome_email: e.target.checked})}
                className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <div>
                <Label htmlFor="send_welcome_email" className="font-medium cursor-pointer" style={{ color: theme.text }}>
                  Send Welcome Email
                </Label>
                <p className="text-xs mt-0.5" style={{ color: theme.textMuted }}>
                  Automatically send a welcome email with login credentials to the owner. 
                  Includes step-by-step instructions on how to get started.
                </p>
              </div>
            </div>
          </div>
          
          <DialogFooter className="pt-4">
            <Button type="button" variant="outline" onClick={handleClose} style={{ borderColor: theme.border, color: theme.textMuted }}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading} style={{ background: theme.primary }}>
              {isLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Create Organization'}
            </Button>
          </DialogFooter>
        </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Organization Detail View
const OrgDetailView = ({ org, token, onClose, onUpdated }) => {
  const [details, setDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({});
  const [activeTab, setActiveTab] = useState('overview');
  const [auditTrail, setAuditTrail] = useState({ e_signatures: [], activities: [] });
  const [loadingAudit, setLoadingAudit] = useState(false);

  const loadDetails = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await adminApi.getOrganization(org.id, token);
      setDetails(res.data);
      const featureFlags = res.data.data.feature_flags || {};
      setFormData({
        company_name: res.data.data.company_name,
        email: res.data.data.email,
        phone: res.data.data.phone || '',
        status: res.data.data.status || 'active',
        relationship_type: res.data.data.relationship_type || 'portal_only',
        service_tier: res.data.data.service_tier || 'portal_free',
        birdenmedia_monthly_fee: res.data.data.birdenmedia_monthly_fee || 0,
        sms_phone_number: res.data.data.sms_settings?.twilio_phone_number || '',
        // Business Type & Niche Features
        business_type: res.data.data.business_type || 'general_contractor',
        secondary_niches: res.data.data.secondary_niches || [],
        default_language: res.data.data.preferences?.default_language || 'en',
        feature_recurring_jobs: featureFlags.recurring_jobs || false,
        feature_route_planning: featureFlags.route_planning || false,
        feature_measurements: featureFlags.measurements || false,
        feature_seasonal_services: featureFlags.seasonal_services || false,
        feature_leads: featureFlags.leads || false,
        feature_follow_ups: featureFlags.follow_ups || false,
        feature_debug_sync_queue: featureFlags.debug_sync_queue || false,
        // Role Permissions & Crew Titles
        role_permissions: res.data.role_permissions?.custom || null,
        crew_titles: res.data.crew_titles || null,
      });
    } catch (error) {
      toast.error('Failed to load details');
    } finally {
      setIsLoading(false);
    }
  }, [org.id, token]);

  useEffect(() => { loadDetails(); }, [loadDetails]);

  const loadAuditTrail = async () => {
    setLoadingAudit(true);
    try {
      const res = await adminApi.getOrgAuditTrail(org.id, token);
      setAuditTrail(res.data);
    } catch (error) {
      console.error('Failed to load audit trail:', error);
    } finally {
      setLoadingAudit(false);
    }
  };

  const handleSave = async () => {
    try {
      // Build the update payload, handling sms_phone_number specially
      const { sms_phone_number, ...baseData } = formData;
      const updatePayload = {
        ...baseData,
        // Merge SMS phone number into sms_settings
        sms_settings: {
          ...(details.data.sms_settings || {}),
          twilio_phone_number: sms_phone_number || null
        }
      };
      await adminApi.updateOrganization(org.id, updatePayload, token);
      toast.success('Organization updated');
      setEditMode(false);
      onUpdated();
      loadDetails();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const handleImpersonate = async (userId) => {
    try {
      const res = await adminApi.impersonateUser(userId, token);
      if (res.data.success) {
        window.open(`/auth/verify?token=${res.data.impersonation_token}`, '_blank');
        toast.success(`Logged in as ${res.data.user.name}`);
      }
    } catch (error) {
      toast.error('Failed to impersonate');
    }
  };

  const handleResetPassword = async (userId) => {
    try {
      const result = await adminApi.resetPassword(userId, token, 'changeme123');
      toast.success(`Password reset to: changeme123`);
    } catch (error) {
      console.error('Reset password error:', error);
      toast.error('Failed to reset password');
    }
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: `${theme.bg}ee` }}>
        <RefreshCw className="w-8 h-8 animate-spin" style={{ color: theme.primary }} />
      </div>
    );
  }

  const tiers = formData.relationship_type === 'birdenmedia_client' 
    ? ['starter', 'growth', 'partner']
    : ['portal_free', 'portal_pro', 'portal_enterprise'];

  return (
    <div className="fixed inset-0 z-50 overflow-auto" style={{ background: theme.bg }}>
      <div className="max-w-6xl mx-auto p-3 sm:p-6">
        {/* Header - Mobile Responsive */}
        <div className="flex items-center justify-between mb-4 sm:mb-6">
          <Button variant="ghost" onClick={onClose} className="h-9 px-2 sm:px-3" style={{ color: theme.textMuted }}>
            <ChevronRight className="w-4 h-4 mr-1 rotate-180" /> <span className="hidden sm:inline">Back</span>
          </Button>
          <div className="flex gap-2">
            {editMode ? (
              <>
                <Button variant="outline" onClick={() => setEditMode(false)} className="h-9 px-2 sm:px-3 text-sm" style={{ borderColor: theme.border, color: theme.textMuted }}>
                  <span className="hidden sm:inline">Cancel</span>
                  <X className="w-4 h-4 sm:hidden" />
                </Button>
                <Button onClick={handleSave} className="h-9 px-2 sm:px-3 text-sm" style={{ background: theme.success }}>Save</Button>
              </>
            ) : (
              <Button onClick={() => setEditMode(true)} className="h-9 px-2 sm:px-3 text-sm" style={{ background: theme.primary }}>
                <Edit className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">Edit</span>
              </Button>
            )}
          </div>
        </div>
        
        {/* Tab Navigation */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          <Button 
            variant={activeTab === 'overview' ? 'default' : 'outline'} 
            onClick={() => setActiveTab('overview')}
            className="h-9 px-4 text-sm whitespace-nowrap"
            style={activeTab === 'overview' ? { background: theme.primary } : { borderColor: theme.border, color: theme.textMuted }}
          >
            <Building className="w-4 h-4 mr-2" /> Overview
          </Button>
          <Button 
            variant={activeTab === 'audit' ? 'default' : 'outline'} 
            onClick={() => { setActiveTab('audit'); loadAuditTrail(); }}
            className="h-9 px-4 text-sm whitespace-nowrap"
            style={activeTab === 'audit' ? { background: theme.primary } : { borderColor: theme.border, color: theme.textMuted }}
          >
            <FileSignature className="w-4 h-4 mr-2" /> Audit Trail
          </Button>
          <Button 
            variant={activeTab === 'estimates' ? 'default' : 'outline'} 
            onClick={() => setActiveTab('estimates')}
            className="h-9 px-4 text-sm whitespace-nowrap"
            style={activeTab === 'estimates' ? { background: theme.primary } : { borderColor: theme.border, color: theme.textMuted }}
            data-testid="admin-estimates-tab"
          >
            <FileText className="w-4 h-4 mr-2" /> Estimates
          </Button>
        </div>

        {activeTab === 'overview' && (
        <>
        {/* Hero Banner — Company Identity */}
        <div className="rounded-2xl p-6 sm:p-8 border mb-6" style={{ background: theme.cardBg, borderColor: theme.border }}>
          <div className="flex flex-col sm:flex-row sm:items-center gap-5">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: `${theme.primary}20` }}>
              <Building2 className="w-8 h-8" style={{ color: theme.primary }} />
            </div>
            <div className="flex-1 min-w-0">
              {editMode ? (
                <Input
                  value={formData.company_name}
                  onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                  className="text-2xl font-bold h-12 border mb-1"
                  style={{ background: theme.bg, borderColor: theme.border, color: theme.text }}
                />
              ) : (
                <h1 className="text-2xl sm:text-3xl font-bold" style={{ color: theme.text }}>{details.data.company_name}</h1>
              )}
              <p className="text-sm mt-1" style={{ color: theme.textDim }}>{org.id}</p>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              {editMode ? (
                <Select value={formData.status} onValueChange={(v) => setFormData({...formData, status: v})}>
                  <SelectTrigger className="w-32 h-9 border" style={{ background: theme.bg, borderColor: theme.border, color: theme.text }}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent style={{ background: theme.cardBg, borderColor: theme.border }}>
                    <SelectItem value="active" style={{ color: theme.text }}>Active</SelectItem>
                    <SelectItem value="paused" style={{ color: theme.text }}>Paused</SelectItem>
                    <SelectItem value="cancelled" style={{ color: theme.text }}>Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <Badge className={`text-sm px-3 py-1 ${details.data.status === 'active' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/30' : details.data.status === 'paused' ? 'bg-amber-500/10 text-amber-400 border border-amber-500/30' : 'bg-red-500/10 text-red-400 border border-red-500/30'}`}>
                  {details.data.status || 'Active'}
                </Badge>
              )}
            </div>
          </div>
          
          {/* Quick Stats Row */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-6 pt-6 border-t" style={{ borderColor: theme.border }}>
            {[
              { label: 'Clients', value: details.counts?.clients, icon: Users, color: theme.primary },
              { label: 'Estimates', value: details.counts?.estimates, icon: FileText, color: '#f59e0b' },
              { label: 'Jobs', value: details.counts?.jobs, icon: Briefcase, color: '#3b82f6' },
              { label: 'Invoices', value: details.counts?.invoices, icon: Receipt, color: '#8b5cf6' },
              { label: 'Crew', value: details.counts?.employees, icon: HardHat, color: '#10b981' },
            ].map(({ label, value, icon: Icon, color }) => (
              <div key={label} className="text-center p-3 rounded-xl" style={{ background: theme.bg }}>
                <Icon className="w-5 h-5 mx-auto mb-1.5" style={{ color }} />
                <p className="text-xl font-bold" style={{ color: theme.text }}>{value || 0}</p>
                <p className="text-xs" style={{ color: theme.textDim }}>{label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Two Column Flow: Contact + Service & Billing */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Contact & Company Info */}
          <div className="rounded-2xl p-6 border" style={{ background: theme.cardBg, borderColor: theme.border }}>
            <h2 className="font-semibold text-base mb-4 flex items-center gap-2" style={{ color: theme.text }}>
              <Mail className="w-4 h-4" style={{ color: theme.primary }} /> Contact Info
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase" style={{ color: theme.textDim }}>Email</Label>
                {editMode ? (
                  <Input value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} className="mt-1 h-9 border" style={{ background: theme.bg, borderColor: theme.border, color: theme.text }} />
                ) : (
                  <p className="mt-1.5 flex items-center gap-2 text-sm" style={{ color: theme.text }}>
                    <Mail className="w-3.5 h-3.5" style={{ color: theme.textDim }} /> {details.data.email}
                  </p>
                )}
              </div>
              <div>
                <Label className="text-xs uppercase" style={{ color: theme.textDim }}>Phone</Label>
                {editMode ? (
                  <Input value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} className="mt-1 h-9 border" style={{ background: theme.bg, borderColor: theme.border, color: theme.text }} />
                ) : (
                  <p className="mt-1.5 flex items-center gap-2 text-sm" style={{ color: theme.text }}>
                    <Phone className="w-3.5 h-3.5" style={{ color: theme.textDim }} /> {details.data.phone || 'Not set'}
                  </p>
                )}
              </div>
              <div>
                <Label className="text-xs uppercase" style={{ color: theme.textDim }}>Industry</Label>
                <p className="mt-1.5 text-sm" style={{ color: theme.text }}>{details.data.industry || 'Not set'}</p>
              </div>
              <div>
                <Label className="text-xs uppercase" style={{ color: theme.textDim }}>SMS Number</Label>
                {editMode ? (
                  <Input type="tel" value={formData.sms_phone_number || ''} onChange={(e) => setFormData({...formData, sms_phone_number: e.target.value})} placeholder="+1 (555) 123-4567" className="mt-1 h-9 border" style={{ background: theme.bg, borderColor: theme.border, color: theme.text }} />
                ) : (
                  <p className="mt-1.5 flex items-center gap-2 text-sm" style={{ color: details.data.sms_settings?.twilio_phone_number ? theme.success : theme.textDim }}>
                    <MessageSquare className="w-3.5 h-3.5" />
                    {details.data.sms_settings?.twilio_phone_number || 'Not assigned'}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Service & Billing */}
          <div className="rounded-2xl p-6 border" style={{ background: theme.cardBg, borderColor: theme.border }}>
            <h2 className="font-semibold text-base mb-4 flex items-center gap-2" style={{ color: theme.text }}>
              <DollarSign className="w-4 h-4" style={{ color: theme.success }} /> Service & Billing
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase" style={{ color: theme.textDim }}>Relationship</Label>
                {editMode ? (
                  <Select value={formData.relationship_type} onValueChange={(v) => setFormData({...formData, relationship_type: v, service_tier: v === 'birdenmedia_client' ? 'starter' : 'portal_free'})}>
                    <SelectTrigger className="mt-1 h-9 border" style={{ background: theme.bg, borderColor: theme.border, color: theme.text }}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent style={{ background: theme.cardBg, borderColor: theme.border }}>
                      <SelectItem value="birdenmedia_client" style={{ color: theme.text }}>BirdenMedia Client</SelectItem>
                      <SelectItem value="portal_only" style={{ color: theme.text }}>Portal Only</SelectItem>
                      <SelectItem value="bm_assist" style={{ color: theme.text }}>BM-Assist</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <Badge className={`mt-1.5 ${relationshipConfig[details.data.relationship_type]?.bgColor} ${relationshipConfig[details.data.relationship_type]?.textColor}`}>
                    {relationshipConfig[details.data.relationship_type]?.label || 'Portal Only'}
                  </Badge>
                )}
              </div>
              <div>
                <Label className="text-xs uppercase" style={{ color: theme.textDim }}>Tier</Label>
                {editMode ? (
                  <Select value={formData.service_tier} onValueChange={(v) => setFormData({...formData, service_tier: v})}>
                    <SelectTrigger className="mt-1 h-9 border" style={{ background: theme.bg, borderColor: theme.border, color: theme.text }}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent style={{ background: theme.cardBg, borderColor: theme.border }}>
                      {tiers.map(t => <SelectItem key={t} value={t} style={{ color: theme.text }}>{tierConfig[t]?.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="mt-1.5 font-semibold" style={{ color: tierConfig[details.data.service_tier]?.color }}>{tierConfig[details.data.service_tier]?.label || 'Portal Free'}</p>
                )}
              </div>
              <div>
                <Label className="text-xs uppercase" style={{ color: theme.textDim }}>Monthly Fee</Label>
                {editMode ? (
                  <Input type="number" value={formData.birdenmedia_monthly_fee} onChange={(e) => setFormData({...formData, birdenmedia_monthly_fee: parseFloat(e.target.value) || 0})} className="mt-1 h-9 border" style={{ background: theme.bg, borderColor: theme.border, color: theme.text }} />
                ) : (
                  <p className="mt-1.5 text-2xl font-bold" style={{ color: theme.success }}>${details.data.birdenmedia_monthly_fee || 0}<span className="text-sm font-normal" style={{ color: theme.textDim }}>/mo</span></p>
                )}
              </div>
              <div>
                <Label className="text-xs uppercase" style={{ color: theme.textDim }}>Financials</Label>
                <div className="mt-1.5 space-y-1">
                  <p className="text-sm"><span style={{ color: theme.textDim }}>Revenue:</span> <span className="font-semibold" style={{ color: theme.success }}>${(details.financials?.total_revenue || 0).toLocaleString()}</span></p>
                  <p className="text-sm"><span style={{ color: theme.textDim }}>Outstanding:</span> <span className="font-semibold" style={{ color: theme.warning }}>${(details.financials?.total_outstanding || 0).toLocaleString()}</span></p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Two Column: Settings & Features + Users */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Org Settings */}
          <div className="rounded-2xl p-6 border" style={{ background: theme.cardBg, borderColor: theme.border }}>
            <h2 className="font-semibold text-base mb-4 flex items-center gap-2" style={{ color: theme.text }}>
              <Settings className="w-4 h-4" style={{ color: theme.secondary }} /> Org Settings
            </h2>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase" style={{ color: theme.textDim }}>Primary Niche</Label>
                  {editMode ? (
                    <Select value={formData.business_type || 'general_contractor'} onValueChange={(v) => setFormData({...formData, business_type: v})}>
                      <SelectTrigger className="mt-1 h-9 border" style={{ background: theme.bg, borderColor: theme.border, color: theme.text }}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent style={{ background: theme.cardBg, borderColor: theme.border }}>
                        {['construction','general_contractor','concrete','roofing','fencing','decking','painting','plumbing','electrical','hvac','landscaping','lawn_care','detailing','cleaning','other'].map(bt => (
                          <SelectItem key={bt} value={bt} style={{ color: theme.text }}>{bt.replace(/_/g,' ').replace(/\b\w/g,c=>c.toUpperCase())}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <p className="mt-1.5 capitalize text-sm" style={{ color: theme.text }}>{(details.data.business_type || 'general_contractor').replace(/_/g, ' ')}</p>
                  )}
                </div>
                <div>
                  <Label className="text-xs uppercase" style={{ color: theme.textDim }}>Default Language</Label>
                  {editMode ? (
                    <Select value={formData.default_language || details?.data?.preferences?.default_language || 'en'} onValueChange={(v) => setFormData({...formData, default_language: v})}>
                      <SelectTrigger className="mt-1 h-9 border" style={{ background: theme.bg, borderColor: theme.border, color: theme.text }}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent style={{ background: theme.cardBg, borderColor: theme.border }}>
                        <SelectItem value="en" style={{ color: theme.text }}>English</SelectItem>
                        <SelectItem value="es" style={{ color: theme.text }}>Espa&ntilde;ol</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <p className="mt-1.5 text-sm" style={{ color: theme.text }}>{(details?.data?.preferences?.default_language || 'en') === 'es' ? 'Espa\u00f1ol' : 'English'}</p>
                  )}
                </div>
              </div>

              {/* Secondary Niches multi-tag picker */}
              <div>
                <Label className="text-xs uppercase" style={{ color: theme.textDim }}>Secondary Niches / Services Offered</Label>
                {editMode ? (
                  <div className="mt-1.5 flex flex-wrap gap-1.5 p-2 rounded-lg border" style={{ background: theme.bg, borderColor: theme.border }} data-testid="admin-secondary-niches-editor">
                    {['construction','concrete','roofing','fencing','decking','painting','plumbing','electrical','hvac','landscaping','lawn_care','detailing','cleaning'].map(n => {
                      const current = formData.secondary_niches || details?.data?.secondary_niches || [];
                      const isPrimary = (formData.business_type || details?.data?.business_type) === n;
                      const isSelected = current.includes(n);
                      const toggle = () => {
                        if (isPrimary) return; // can't add primary as secondary
                        const next = isSelected ? current.filter(x => x !== n) : [...current, n];
                        setFormData({ ...formData, secondary_niches: next });
                      };
                      return (
                        <button
                          key={n}
                          type="button"
                          onClick={toggle}
                          disabled={isPrimary}
                          className="text-xs px-2.5 py-1 rounded-full border transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                          style={{
                            background: isSelected ? theme.secondary : 'transparent',
                            color: isSelected ? '#fff' : theme.text,
                            borderColor: isSelected ? theme.secondary : theme.border,
                          }}
                          data-testid={`admin-niche-toggle-${n}`}
                        >
                          {isSelected ? '✓ ' : '+ '}{n.replace(/_/g,' ').replace(/\b\w/g,c=>c.toUpperCase())}
                        </button>
                      );
                    })}
                    <p className="w-full text-[11px] mt-1.5" style={{ color: theme.textDim }}>
                      Click to toggle. Primary niche is locked from this list. Saved tags drive measurement options, photo categories &amp; checklists for the contractor.
                    </p>
                  </div>
                ) : (
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {(details?.data?.secondary_niches || []).length === 0 ? (
                      <span className="text-xs italic" style={{ color: theme.textDim }}>None set</span>
                    ) : (
                      (details?.data?.secondary_niches || []).map(n => (
                        <span key={n} className="text-xs px-2 py-0.5 rounded-full" style={{ background: theme.bg, color: theme.text, border: `1px solid ${theme.border}` }}>
                          {n.replace(/_/g,' ').replace(/\b\w/g,c=>c.toUpperCase())}
                        </span>
                      ))
                    )}
                  </div>
                )}
              </div>
              
              {/* Feature Toggles - Compact Grid */}
              <div className="pt-3 border-t" style={{ borderColor: theme.border }}>
                <Label className="text-xs uppercase mb-2 block" style={{ color: theme.textDim }}>Feature Toggles</Label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { key: 'feature_recurring_jobs', label: 'Recurring Jobs', icon: RefreshCw, flag: 'recurring_jobs' },
                    { key: 'feature_route_planning', label: 'Route Planning', icon: MapPin, flag: 'route_planning' },
                    { key: 'feature_measurements', label: 'Measurements', icon: ClipboardList, flag: 'measurements' },
                    { key: 'feature_seasonal_services', label: 'Seasonal', icon: Calendar, flag: 'seasonal_services' },
                    { key: 'feature_leads', label: 'Lead Dashboard', icon: TrendingUp, flag: 'leads' },
                    { key: 'feature_follow_ups', label: 'Auto Follow-Ups', icon: RefreshCw, flag: 'follow_ups' },
                    { key: 'feature_debug_sync_queue', label: 'Developer Mode', icon: RefreshCw, flag: 'debug_sync_queue' },
                  ].map(({ key, label, icon: FIcon, flag }) => (
                    <div key={key} className="flex items-center justify-between p-2 rounded-lg" style={{ background: theme.bg }}>
                      <div className="flex items-center gap-2">
                        <FIcon className="w-3.5 h-3.5" style={{ color: theme.primary }} />
                        <span className="text-xs" style={{ color: theme.text }}>{label}</span>
                      </div>
                      {editMode ? (
                        <Switch checked={formData[key] || false} onCheckedChange={(checked) => setFormData({...formData, [key]: checked})} className="scale-75" />
                      ) : (
                        <span className={`text-xs font-medium ${details.data.feature_flags?.[flag] ? 'text-emerald-400' : 'text-gray-500'}`}>
                          {details.data.feature_flags?.[flag] ? 'On' : 'Off'}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Users */}
          <div className="rounded-2xl p-6 border" style={{ background: theme.cardBg, borderColor: theme.border }}>
            <h2 className="font-semibold text-base mb-4 flex items-center gap-2" style={{ color: theme.text }}>
              <Users className="w-4 h-4" style={{ color: theme.primary }} />
              Users ({details.users?.length || 0})
            </h2>
            <div className="space-y-2 max-h-[340px] overflow-y-auto pr-1">
              {details.users?.map(user => (
                <div key={user.id} className="flex items-center justify-between p-2.5 rounded-xl" style={{ background: theme.bg }}>
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-medium text-sm flex-shrink-0" style={{ background: theme.primary }}>
                      {user.name?.charAt(0)?.toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-sm truncate" style={{ color: theme.text }}>{user.name}</p>
                      <p className="text-xs truncate" style={{ color: theme.textMuted }}>{user.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
                    <Badge variant="outline" className={`text-[10px] ${user.role === 'owner' ? 'border-amber-500 text-amber-400' : ''}`} style={{ borderColor: user.role === 'owner' ? undefined : theme.border, color: user.role === 'owner' ? undefined : theme.textMuted }}>
                      {user.role === 'owner' && <Crown className="w-2.5 h-2.5 mr-0.5" />}{user.role}
                    </Badge>
                    <Button size="sm" variant="ghost" onClick={() => handleImpersonate(user.id)} className="h-7 w-7 p-0" style={{ color: theme.primary }}><LogIn className="w-3.5 h-3.5" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => handleResetPassword(user.id)} className="h-7 w-7 p-0" style={{ color: theme.textMuted }}><Key className="w-3.5 h-3.5" /></Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Full Width: Role Permissions */}
        <div className="rounded-2xl p-6 border mb-6" style={{ background: theme.cardBg, borderColor: theme.border }}>
          <h2 className="font-semibold text-base mb-4 flex items-center gap-2" style={{ color: theme.text }}>
            <Shield className="w-4 h-4" style={{ color: theme.secondary }} /> Role Permissions & Crew Titles
          </h2>
          <Tabs defaultValue="staff" className="w-full">
            <TabsList className="grid w-full grid-cols-3 mb-4" style={{ background: theme.bg }}>
              <TabsTrigger value="staff" className="text-xs sm:text-sm">Staff</TabsTrigger>
              <TabsTrigger value="manager" className="text-xs sm:text-sm">Manager</TabsTrigger>
              <TabsTrigger value="admin" className="text-xs sm:text-sm">Admin</TabsTrigger>
            </TabsList>
            {['staff', 'manager', 'admin'].map((role) => (
              <TabsContent key={role} value={role} className="space-y-3">
                <div className="p-3 rounded-lg text-xs" style={{ background: theme.bg, color: theme.textDim }}>
                  <strong className="capitalize" style={{ color: theme.text }}>{role}</strong>: {
                    role === 'staff' ? 'Basic access - view assigned work, update status, add notes/photos' :
                    role === 'manager' ? 'Full operational access - estimates, invoices, jobs, crew' :
                    'Full access including settings, users, billing'
                  }
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {[
                    { label: 'Jobs & Site Visits', perms: ['view_assigned_jobs', 'view_assigned_site_visits', 'update_job_status', 'complete_site_visits', 'create_jobs', 'assign_crew'] },
                    { label: 'Notes & Media', perms: ['add_notes', 'add_photos', 'add_sketches', 'add_measurements'] },
                    { label: 'Estimates', perms: ['create_draft_estimates', 'edit_draft_estimates', 'send_estimates', 'view_all_estimates'] },
                    { label: 'Invoices & Payments', perms: ['create_invoices', 'send_invoices', 'record_payments'] },
                    { label: 'Customers', perms: ['view_customers', 'create_customers', 'archive_customers'] },
                    { label: 'Admin', perms: ['manage_users', 'access_settings', 'manage_billing', 'delete_records', 'export_data', 'view_reports', 'manage_templates'] },
                  ].map(({ label, perms }) => (
                    <div key={label} className="p-3 rounded-xl" style={{ background: theme.bg }}>
                      <p className="text-xs font-semibold mb-2" style={{ color: theme.textMuted }}>{label}</p>
                      <div className="space-y-1">
                        {perms.map((perm) => {
                          const effectivePerms = details.role_permissions?.effective?.[role] || {};
                          const formPerms = formData.role_permissions?.[role] || {};
                          const isEnabled = editMode 
                            ? (formPerms[perm] !== undefined ? formPerms[perm] : effectivePerms[perm])
                            : effectivePerms[perm];
                          const permLabel = perm.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                          return (
                            <div key={perm} className="flex items-center justify-between py-0.5">
                              <span className="text-xs" style={{ color: isEnabled ? theme.success : theme.textDim }}>{permLabel}</span>
                              {editMode ? (
                                <Switch checked={!!isEnabled} onCheckedChange={(checked) => {
                                  const newPerms = { ...(formData.role_permissions || {}) };
                                  if (!newPerms[role]) newPerms[role] = { ...(details.role_permissions?.effective?.[role] || {}) };
                                  newPerms[role][perm] = checked;
                                  setFormData({ ...formData, role_permissions: newPerms });
                                }} className="scale-[0.65]" />
                              ) : (
                                <span className={`text-xs ${isEnabled ? 'text-emerald-400' : 'text-gray-500'}`}>{isEnabled ? '✓' : '✗'}</span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </TabsContent>
            ))}
          </Tabs>
          {/* Crew Titles */}
          <div className="mt-5 pt-5 border-t" style={{ borderColor: theme.border }}>
            <Label className="text-xs uppercase mb-2 block" style={{ color: theme.textDim }}>Crew Titles (Field Roles)</Label>
            <div className="flex flex-wrap gap-2">
              {(editMode ? (formData.crew_titles || details.crew_titles || ['Estimator', 'Project Manager', 'Crew Lead', 'Crew Member', 'Subcontractor']) : (details.crew_titles || ['Estimator', 'Project Manager', 'Crew Lead', 'Crew Member', 'Subcontractor'])).map((title, idx) => (
                <Badge key={idx} className={`text-xs ${editMode ? 'cursor-pointer hover:bg-red-500/20 pr-1' : ''} bg-blue-500/10 text-blue-400`}
                  onClick={() => { if (editMode) { const ct = formData.crew_titles || details.crew_titles || ['Estimator', 'Project Manager', 'Crew Lead', 'Crew Member', 'Subcontractor']; setFormData({ ...formData, crew_titles: ct.filter((_, i) => i !== idx) }); } }}>
                  {title}{editMode && <X className="w-3 h-3 ml-1 inline hover:text-red-400" />}
                </Badge>
              ))}
            </div>
            {editMode && (
              <Input placeholder="Add crew title, press Enter" className="text-sm h-8 mt-3 max-w-xs" style={{ background: theme.bg, borderColor: theme.border, color: theme.text }}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); const v = e.target.value.trim(); if (v) { const ex = formData.crew_titles || details.crew_titles || ['Estimator', 'Project Manager', 'Crew Lead', 'Crew Member', 'Subcontractor']; if (!ex.includes(v)) setFormData({ ...formData, crew_titles: [...ex, v] }); e.target.value = ''; } }} } />
            )}
          </div>
        </div>
        
        {/* Invoicing Section - Full Width */}
        <OrgInvoiceSection orgId={org.id} orgName={org.company_name} monthlyRate={details.data.birdenmedia_monthly_fee || 0} token={token} />
        </>
        )}

        {/* AUDIT TRAIL TAB */}
        {activeTab === 'audit' && (
          <div className="space-y-6">
            {/* E-Signatures Section */}
            <div className="rounded-xl p-4 sm:p-6 border" style={{ background: theme.cardBg, borderColor: theme.border }}>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${theme.primary}20` }}>
                  <FileText className="w-5 h-5" style={{ color: theme.primary }} />
                </div>
                <div>
                  <h2 className="font-semibold text-lg" style={{ color: theme.text }}>E-Signature Audit Trail</h2>
                  <p className="text-sm" style={{ color: theme.textMuted }}>All electronically signed estimates for this organization</p>
                </div>
              </div>

              {loadingAudit ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-8 h-8 animate-spin" style={{ color: theme.primary }} />
                </div>
              ) : auditTrail.e_signatures?.length === 0 ? (
                <div className="text-center py-12" style={{ background: theme.bg, borderRadius: '12px' }}>
                  <FileText className="w-12 h-12 mx-auto mb-4" style={{ color: theme.textDim }} />
                  <p style={{ color: theme.textMuted }}>No e-signatures yet</p>
                  <p className="text-sm mt-1" style={{ color: theme.textDim }}>When clients e-sign estimates, they'll appear here</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {auditTrail.e_signatures.map((sig) => (
                    <div 
                      key={sig.id} 
                      className="p-4 rounded-lg border"
                      style={{ background: theme.bg, borderColor: theme.border }}
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-semibold" style={{ color: theme.text }}>
                              {sig.client_name || 'Client'}
                            </span>
                            <span 
                              className="text-xs px-2 py-0.5 rounded-full"
                              style={{ background: `${theme.success}20`, color: theme.success }}
                            >
                              Signed
                            </span>
                          </div>
                          <p className="text-sm" style={{ color: theme.textMuted }}>
                            Estimate #{sig.id?.slice(-8)} • ${(sig.total || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-medium text-sm" style={{ color: theme.text }}>
                            {sig.e_signature?.signer_name}
                          </p>
                          <p className="text-xs" style={{ color: theme.textDim }}>
                            {sig.e_signature?.signed_at ? new Date(sig.e_signature.signed_at).toLocaleString() : 'N/A'}
                          </p>
                        </div>
                      </div>
                      
                      {/* Signature Details (Expandable) */}
                      <details className="mt-3 pt-3" style={{ borderTop: `1px solid ${theme.border}` }}>
                        <summary className="cursor-pointer text-sm font-medium" style={{ color: theme.primary }}>
                          View Signature Details
                        </summary>
                        <div 
                          className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm p-4 rounded-lg"
                          style={{ background: theme.cardBg }}
                        >
                          <div className="flex items-center gap-2">
                            <Users className="w-4 h-4" style={{ color: theme.textDim }} />
                            <span style={{ color: theme.textMuted }}>Signer:</span>
                            <span className="font-medium" style={{ color: theme.text }}>{sig.e_signature?.signer_name}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Mail className="w-4 h-4" style={{ color: theme.textDim }} />
                            <span style={{ color: theme.textMuted }}>Email:</span>
                            <span className="font-medium" style={{ color: theme.text }}>{sig.e_signature?.signer_email || 'N/A'}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4" style={{ color: theme.textDim }} />
                            <span style={{ color: theme.textMuted }}>Signed:</span>
                            <span className="font-medium" style={{ color: theme.text }}>
                              {sig.e_signature?.signed_at ? new Date(sig.e_signature.signed_at).toLocaleString() : 'N/A'}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Activity className="w-4 h-4" style={{ color: theme.textDim }} />
                            <span style={{ color: theme.textMuted }}>IP:</span>
                            <span className="font-mono text-xs" style={{ color: theme.text }}>{sig.e_signature?.ip_address || 'N/A'}</span>
                          </div>
                          <div className="flex items-center gap-2 sm:col-span-2">
                            <Shield className="w-4 h-4" style={{ color: theme.textDim }} />
                            <span style={{ color: theme.textMuted }}>Document Hash:</span>
                            <span className="font-mono text-xs truncate" style={{ color: theme.text }}>{sig.e_signature?.document_hash || 'N/A'}</span>
                          </div>
                          <div className="flex items-center gap-2 sm:col-span-2">
                            <CheckCircle className="w-4 h-4" style={{ color: theme.success }} />
                            <span className="font-medium" style={{ color: theme.success }}>
                              {sig.e_signature?.terms_accepted ? 'Terms & Conditions Accepted' : 'Terms not recorded'}
                            </span>
                          </div>
                        </div>
                      </details>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Activity Log Section */}
            <div className="rounded-xl p-4 sm:p-6 border" style={{ background: theme.cardBg, borderColor: theme.border }}>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${theme.secondary}20` }}>
                  <Activity className="w-5 h-5" style={{ color: theme.secondary }} />
                </div>
                <div>
                  <h2 className="font-semibold text-lg" style={{ color: theme.text }}>Recent Activity</h2>
                  <p className="text-sm" style={{ color: theme.textMuted }}>Latest actions in this organization</p>
                </div>
              </div>

              {auditTrail.activities?.length === 0 ? (
                <div className="text-center py-8" style={{ background: theme.bg, borderRadius: '12px' }}>
                  <Activity className="w-10 h-10 mx-auto mb-3" style={{ color: theme.textDim }} />
                  <p style={{ color: theme.textMuted }}>No activity recorded</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {auditTrail.activities?.slice(0, 20).map((activity) => (
                    <div 
                      key={activity.id}
                      className="flex items-start gap-3 p-3 rounded-lg"
                      style={{ background: theme.bg }}
                    >
                      <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: `${theme.primary}20` }}>
                        <Activity className="w-4 h-4" style={{ color: theme.primary }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm" style={{ color: theme.text }}>
                          <span className="font-medium">{activity.user_name || 'System'}</span>
                          {' '}{activity.action}
                        </p>
                        <p className="text-xs mt-0.5" style={{ color: theme.textDim }}>
                          {activity.timestamp ? new Date(activity.timestamp).toLocaleString() : 'N/A'}
                        </p>
                      </div>
                      <span 
                        className="text-xs px-2 py-1 rounded-full flex-shrink-0"
                        style={{ background: `${theme.primary}20`, color: theme.primary }}
                      >
                        {activity.object_type}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ESTIMATES TAB - Admin Override */}
        {activeTab === 'estimates' && (
          <AdminEstimateOverridePanel orgId={org.id} token={token} />
        )}
      </div>
    </div>
  );
};

// Admin Estimate Override Panel
const AdminEstimateOverridePanel = ({ orgId, token }) => {
  const [estimates, setEstimates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedEstimate, setSelectedEstimate] = useState(null);
  const [overrideForm, setOverrideForm] = useState({ display_mode: '', status: '', reason: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [auditHistory, setAuditHistory] = useState([]);
  const [showAuditModal, setShowAuditModal] = useState(false);

  const loadEstimates = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await adminApi.getOrgEstimates(orgId, token, statusFilter);
      setEstimates(res.data || []);
    } catch (error) {
      toast.error('Failed to load estimates');
    } finally {
      setIsLoading(false);
    }
  }, [orgId, token, statusFilter]);

  useEffect(() => { loadEstimates(); }, [loadEstimates]);

  const handleOverride = async () => {
    if (!overrideForm.reason.trim()) {
      toast.error('Reason is required for admin overrides');
      return;
    }
    if (!overrideForm.display_mode && !overrideForm.status) {
      toast.error('Select at least one field to override');
      return;
    }
    setIsSubmitting(true);
    try {
      const payload = { reason: overrideForm.reason };
      if (overrideForm.display_mode) payload.display_mode = overrideForm.display_mode;
      if (overrideForm.status) payload.status = overrideForm.status;
      await adminApi.overrideEstimate(orgId, selectedEstimate.id, payload, token);
      toast.success('Estimate overridden successfully');
      setSelectedEstimate(null);
      setOverrideForm({ display_mode: '', status: '', reason: '' });
      loadEstimates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to override estimate');
    } finally {
      setIsSubmitting(false);
    }
  };

  const loadAuditHistory = async (estimateId) => {
    try {
      const res = await adminApi.getEstimateAuditHistory(orgId, estimateId, token);
      setAuditHistory(res.data.audit_history || []);
      setShowAuditModal(true);
    } catch {
      toast.error('Failed to load audit history');
    }
  };

  const statusColors = {
    draft: 'bg-gray-500/20 text-gray-400',
    sent: 'bg-blue-500/20 text-blue-400',
    approved: 'bg-green-500/20 text-green-400',
    declined: 'bg-red-500/20 text-red-400'
  };

  return (
    <div className="space-y-4">
      <div className="rounded-xl p-4 sm:p-6 border" style={{ background: theme.cardBg, borderColor: theme.border }}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold text-lg" style={{ color: theme.text }}>Estimate Overrides</h2>
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="h-8 px-3 text-sm rounded-md border"
            style={{ background: theme.bg, borderColor: theme.border, color: theme.text }}
            data-testid="admin-estimate-status-filter"
          >
            <option value="">All Statuses</option>
            <option value="draft">Draft</option>
            <option value="sent">Sent</option>
            <option value="approved">Approved</option>
            <option value="declined">Declined</option>
          </select>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin" style={{ color: theme.primary }} />
          </div>
        ) : estimates.length === 0 ? (
          <p className="text-center py-8" style={{ color: theme.textMuted }}>No estimates found</p>
        ) : (
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {estimates.map(est => (
              <div
                key={est.id}
                className="flex items-center justify-between p-3 rounded-lg border"
                style={{ background: theme.bg, borderColor: theme.border }}
                data-testid={`admin-estimate-row-${est.id}`}
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div>
                    <p className="text-sm font-medium truncate" style={{ color: theme.text }}>
                      #{est.id?.slice(0, 8).toUpperCase()} - {est.service_type || 'Estimate'}
                    </p>
                    <p className="text-xs" style={{ color: theme.textMuted }}>
                      {est.client_name} | {est.display_mode} | ${(est.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-1 rounded-full ${statusColors[est.status] || 'bg-gray-500/20 text-gray-400'}`}>
                    {est.status}
                  </span>
                  {est.admin_overridden && (
                    <span className="text-xs px-2 py-1 rounded-full bg-amber-500/20 text-amber-400">
                      Overridden
                    </span>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => loadAuditHistory(est.id)}
                    className="h-7 px-2"
                    style={{ color: theme.textMuted }}
                    data-testid={`admin-estimate-audit-${est.id}`}
                  >
                    <ClipboardList className="w-3.5 h-3.5" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setSelectedEstimate(est);
                      setOverrideForm({ display_mode: '', status: '', reason: '' });
                    }}
                    className="h-7 px-3 text-xs"
                    style={{ borderColor: theme.primary, color: theme.primary }}
                    data-testid={`admin-estimate-override-btn-${est.id}`}
                  >
                    <Shield className="w-3 h-3 mr-1" /> Override
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Override Modal */}
      <Dialog open={!!selectedEstimate} onOpenChange={(o) => !o && setSelectedEstimate(null)}>
        <DialogContent className="max-w-md" data-testid="admin-override-modal">
          <DialogHeader>
            <DialogTitle className="text-base font-semibold flex items-center gap-2">
              <Shield className="w-5 h-5 text-amber-500" />
              Admin Override — #{selectedEstimate?.id?.slice(0, 8).toUpperCase()}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="p-3 rounded-lg text-sm" style={{ background: theme.bg, color: theme.textMuted }}>
              <p>Current: <strong style={{ color: theme.text }}>{selectedEstimate?.display_mode}</strong> mode, status: <strong style={{ color: theme.text }}>{selectedEstimate?.status}</strong></p>
              <p className="mt-1">Client: {selectedEstimate?.client_name} | Total: ${(selectedEstimate?.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
            </div>
            <div>
              <Label className="text-sm" style={{ color: theme.text }}>Display Mode</Label>
              <select
                value={overrideForm.display_mode}
                onChange={e => setOverrideForm(prev => ({ ...prev, display_mode: e.target.value }))}
                className="w-full h-9 px-3 mt-1 text-sm rounded-md border"
                style={{ background: theme.bg, borderColor: theme.border, color: theme.text }}
                data-testid="override-display-mode"
              >
                <option value="">No change</option>
                <option value="simple">Simple</option>
                <option value="detailed">Detailed</option>
                <option value="summary">Summary</option>
                <option value="sections">Combined</option>
              </select>
            </div>
            <div>
              <Label className="text-sm" style={{ color: theme.text }}>Status</Label>
              <select
                value={overrideForm.status}
                onChange={e => setOverrideForm(prev => ({ ...prev, status: e.target.value }))}
                className="w-full h-9 px-3 mt-1 text-sm rounded-md border"
                style={{ background: theme.bg, borderColor: theme.border, color: theme.text }}
                data-testid="override-status"
              >
                <option value="">No change</option>
                <option value="draft">Draft</option>
                <option value="sent">Sent</option>
                <option value="approved">Force Approve</option>
                <option value="declined">Force Decline</option>
              </select>
            </div>
            <div>
              <Label className="text-sm" style={{ color: theme.text }}>Reason (required)</Label>
              <Textarea
                value={overrideForm.reason}
                onChange={e => setOverrideForm(prev => ({ ...prev, reason: e.target.value }))}
                placeholder="Explain why this override is needed..."
                className="mt-1 text-sm"
                style={{ background: theme.bg, borderColor: theme.border, color: theme.text }}
                data-testid="override-reason"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedEstimate(null)}>Cancel</Button>
            <Button 
              onClick={handleOverride} 
              disabled={isSubmitting || !overrideForm.reason.trim()}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="override-submit-btn"
            >
              {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Shield className="w-4 h-4 mr-2" />}
              Apply Override
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Audit History Modal */}
      <Dialog open={showAuditModal} onOpenChange={setShowAuditModal}>
        <DialogContent className="max-w-md" data-testid="admin-audit-history-modal">
          <DialogHeader>
            <DialogTitle className="text-base font-semibold">Override Audit History</DialogTitle>
          </DialogHeader>
          {auditHistory.length === 0 ? (
            <p className="text-center py-6 text-sm" style={{ color: theme.textMuted }}>No admin overrides recorded</p>
          ) : (
            <div className="space-y-3 max-h-80 overflow-y-auto">
              {auditHistory.map((entry, idx) => (
                <div key={entry.id || idx} className="p-3 rounded-lg border text-sm" style={{ background: theme.bg, borderColor: theme.border }}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-amber-400">Admin Override</span>
                    <span className="text-xs" style={{ color: theme.textDim }}>
                      {entry.timestamp ? new Date(entry.timestamp).toLocaleString() : 'N/A'}
                    </span>
                  </div>
                  <p style={{ color: theme.textMuted }}>Reason: {entry.reason}</p>
                  {Object.entries(entry.changes || {}).map(([field, change]) => (
                    <p key={field} className="mt-1" style={{ color: theme.text }}>
                      <span className="capitalize">{field.replace('_', ' ')}</span>: 
                      <span className="text-red-400 line-through ml-1">{change.from}</span> 
                      <span className="mx-1">&rarr;</span>
                      <span className="text-green-400">{change.to}</span>
                    </p>
                  ))}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Email Simulator Panel (Internal Tool)
const EmailSimulatorPanel = ({ token }) => {
  const [senderMode, setSenderMode] = useState('random');
  const [selectedPersona, setSelectedPersona] = useState('');
  const [category, setCategory] = useState('general_checkin');
  const [generateCount, setGenerateCount] = useState(3);
  const [generatedEmails, setGeneratedEmails] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSending, setIsSending] = useState({});
  const [personas, setPersonas] = useState([]);
  const [categories, setCategories] = useState([]);
  const [scheduledEmails, setScheduledEmails] = useState([]);
  const [isScheduling, setIsScheduling] = useState(false);
  const [scheduleSettings, setScheduleSettings] = useState({
    minInterval: 5,
    maxInterval: 30,
    startDelay: 1
  });

  // Load personas and categories
  useEffect(() => {
    const loadData = async () => {
      try {
        const response = await adminApi.getEmailSimulatorData(token);
        const data = response.data || response;
        setPersonas(data.personas || []);
        setCategories(data.categories || []);
        if (data.categories?.length > 0) {
          setCategory(data.categories[0].id);
        }
      } catch (error) {
        console.error('Failed to load email simulator data:', error);
      }
    };
    loadData();
  }, [token]);

  const handleGenerate = async () => {
    setIsGenerating(true);
    try {
      const response = await adminApi.generateClientEmails(token, {
        sender_mode: senderMode,
        persona_id: senderMode === 'persona' ? selectedPersona : null,
        category,
        count: generateCount
      });
      const data = response.data || response;
      setGeneratedEmails(prev => [...data.emails, ...prev]);
      toast.success(`Generated ${data.emails.length} email(s)`);
    } catch (error) {
      console.error('Failed to generate emails:', error);
      toast.error('Failed to generate emails');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSend = async (email, index) => {
    setIsSending(prev => ({ ...prev, [index]: true }));
    try {
      await adminApi.sendSimulatedEmail(token, {
        from_name: email.from_name,
        from_company: email.from_company,
        subject: email.subject,
        body: email.body
      });
      toast.success(`Email sent to joe@birdenmedia.co`);
      // Remove sent email from list
      setGeneratedEmails(prev => prev.filter((_, i) => i !== index));
    } catch (error) {
      console.error('Failed to send email:', error);
      toast.error('Failed to send email');
    } finally {
      setIsSending(prev => ({ ...prev, [index]: false }));
    }
  };

  const handleSendAll = async () => {
    for (let i = 0; i < generatedEmails.length; i++) {
      await handleSend(generatedEmails[i], i);
      // Small delay between sends
      await new Promise(r => setTimeout(r, 500));
    }
  };

  const updateEmail = (index, field, value) => {
    setGeneratedEmails(prev => prev.map((email, i) => 
      i === index ? { ...email, [field]: value } : email
    ));
  };

  const deleteEmail = (index) => {
    setGeneratedEmails(prev => prev.filter((_, i) => i !== index));
  };

  // Schedule emails to send at random intervals
  const handleScheduleEmails = async () => {
    if (generatedEmails.length === 0) {
      toast.error('Generate some emails first!');
      return;
    }
    
    setIsScheduling(true);
    try {
      const response = await adminApi.scheduleSimulatedEmails(token, {
        emails: generatedEmails.map(e => ({
          from_name: e.from_name,
          from_company: e.from_company,
          subject: e.subject,
          body: e.body
        })),
        min_interval_minutes: scheduleSettings.minInterval,
        max_interval_minutes: scheduleSettings.maxInterval,
        start_delay_minutes: scheduleSettings.startDelay
      });
      const data = response.data || response;
      setScheduledEmails(data.scheduled_emails || []);
      setGeneratedEmails([]);
      toast.success(`Scheduled ${data.scheduled_emails?.length || 0} emails to send automatically`);
    } catch (error) {
      console.error('Failed to schedule emails:', error);
      toast.error('Failed to schedule emails');
    } finally {
      setIsScheduling(false);
    }
  };

  // Load scheduled emails status
  const refreshScheduledEmails = async () => {
    try {
      const response = await adminApi.getScheduledEmails(token);
      const data = response.data || response;
      setScheduledEmails(data.scheduled_emails || []);
    } catch (error) {
      console.error('Failed to load scheduled emails:', error);
    }
  };

  // Clear all scheduled emails
  const handleClearScheduled = async () => {
    try {
      await adminApi.clearScheduledEmails(token);
      setScheduledEmails([]);
      toast.success('Cleared all scheduled emails');
    } catch (error) {
      console.error('Failed to clear scheduled emails:', error);
      toast.error('Failed to clear scheduled emails');
    }
  };

  const selectedPersonaData = personas.find(p => p.id === selectedPersona);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold" style={{ color: theme.text }}>
            <Sparkles className="w-6 h-6 inline mr-2 text-purple-400" />
            Client Email Simulator
          </h2>
          <p className="text-sm mt-1" style={{ color: theme.textMuted }}>
            Generate and send realistic client emails for testing and demos
          </p>
        </div>
      </div>

      {/* Controls Panel */}
      <div className="rounded-xl border p-4 sm:p-6 space-y-4" style={{ background: theme.cardBg, borderColor: theme.border }}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Sender Mode */}
          <div>
            <Label className="text-sm" style={{ color: theme.textMuted }}>Sender Mode</Label>
            <Select value={senderMode} onValueChange={setSenderMode}>
              <SelectTrigger className="mt-1" style={{ background: theme.bg, borderColor: theme.border, color: theme.text }}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent style={{ background: theme.cardBg, borderColor: theme.border }}>
                <SelectItem value="random" style={{ color: theme.text }}>🎲 Random Client</SelectItem>
                <SelectItem value="persona" style={{ color: theme.text }}>👤 Specific Persona</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Persona Selection (if specific mode) */}
          {senderMode === 'persona' && (
            <div>
              <Label className="text-sm" style={{ color: theme.textMuted }}>Select Persona</Label>
              <Select value={selectedPersona} onValueChange={setSelectedPersona}>
                <SelectTrigger className="mt-1" style={{ background: theme.bg, borderColor: theme.border, color: theme.text }}>
                  <SelectValue placeholder="Choose persona..." />
                </SelectTrigger>
                <SelectContent style={{ background: theme.cardBg, borderColor: theme.border }}>
                  {personas.map(persona => (
                    <SelectItem key={persona.id} value={persona.id} style={{ color: theme.text }}>
                      {persona.name} – {persona.company}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Category */}
          <div>
            <Label className="text-sm" style={{ color: theme.textMuted }}>Email Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="mt-1" style={{ background: theme.bg, borderColor: theme.border, color: theme.text }}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent style={{ background: theme.cardBg, borderColor: theme.border }}>
                {categories.map(cat => (
                  <SelectItem key={cat.id} value={cat.id} style={{ color: theme.text }}>
                    {cat.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Generate Count */}
          <div>
            <Label className="text-sm" style={{ color: theme.textMuted }}>Generate Count</Label>
            <Select value={String(generateCount)} onValueChange={v => setGenerateCount(Number(v))}>
              <SelectTrigger className="mt-1" style={{ background: theme.bg, borderColor: theme.border, color: theme.text }}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent style={{ background: theme.cardBg, borderColor: theme.border }}>
                <SelectItem value="1" style={{ color: theme.text }}>Generate 1</SelectItem>
                <SelectItem value="3" style={{ color: theme.text }}>Generate 3</SelectItem>
                <SelectItem value="5" style={{ color: theme.text }}>Generate 5</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Persona Info (if selected) */}
        {senderMode === 'persona' && selectedPersonaData && (
          <div className="p-4 rounded-lg" style={{ background: theme.bg, borderColor: theme.border }}>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: `${theme.primary}30` }}>
                <User className="w-5 h-5" style={{ color: theme.primary }} />
              </div>
              <div>
                <h4 className="font-semibold" style={{ color: theme.text }}>{selectedPersonaData.name}</h4>
                <p className="text-sm" style={{ color: theme.textMuted }}>{selectedPersonaData.company}</p>
                <p className="text-xs mt-1" style={{ color: theme.textDim }}>{selectedPersonaData.tone_guidance}</p>
              </div>
            </div>
          </div>
        )}

        {/* Generate Button */}
        <div className="flex flex-wrap gap-3">
          <Button
            onClick={handleGenerate}
            disabled={isGenerating || (senderMode === 'persona' && !selectedPersona)}
            className="gap-2"
            style={{ background: theme.primary }}
          >
            {isGenerating ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4" />
                Generate {generateCount} Email{generateCount > 1 ? 's' : ''}
              </>
            )}
          </Button>
          
          {generatedEmails.length > 0 && (
            <>
              <Button
                onClick={handleSendAll}
                variant="outline"
                className="gap-2"
                style={{ borderColor: theme.success, color: theme.success }}
              >
                <Send className="w-4 h-4" />
                Send All Now ({generatedEmails.length})
              </Button>
              
              <Button
                onClick={handleScheduleEmails}
                disabled={isScheduling}
                variant="outline"
                className="gap-2"
                style={{ borderColor: theme.secondary, color: theme.secondary }}
              >
                {isScheduling ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Clock className="w-4 h-4" />
                )}
                Schedule Random Delivery
              </Button>
            </>
          )}
        </div>

        {/* Schedule Settings (collapsed) */}
        {generatedEmails.length > 0 && (
          <div className="p-3 rounded-lg border" style={{ background: theme.bg, borderColor: theme.border }}>
            <p className="text-xs font-medium mb-2" style={{ color: theme.textMuted }}>Schedule Settings (for random delivery)</p>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs" style={{ color: theme.textDim }}>Min Interval (min)</Label>
                <Input
                  type="number"
                  min="1"
                  value={scheduleSettings.minInterval}
                  onChange={e => setScheduleSettings(prev => ({ ...prev, minInterval: parseInt(e.target.value) || 5 }))}
                  className="h-8 text-sm"
                  style={{ background: theme.cardBg, borderColor: theme.border, color: theme.text }}
                />
              </div>
              <div>
                <Label className="text-xs" style={{ color: theme.textDim }}>Max Interval (min)</Label>
                <Input
                  type="number"
                  min="1"
                  value={scheduleSettings.maxInterval}
                  onChange={e => setScheduleSettings(prev => ({ ...prev, maxInterval: parseInt(e.target.value) || 30 }))}
                  className="h-8 text-sm"
                  style={{ background: theme.cardBg, borderColor: theme.border, color: theme.text }}
                />
              </div>
              <div>
                <Label className="text-xs" style={{ color: theme.textDim }}>Start Delay (min)</Label>
                <Input
                  type="number"
                  min="0"
                  value={scheduleSettings.startDelay}
                  onChange={e => setScheduleSettings(prev => ({ ...prev, startDelay: parseInt(e.target.value) || 1 }))}
                  className="h-8 text-sm"
                  style={{ background: theme.cardBg, borderColor: theme.border, color: theme.text }}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Scheduled Emails Status */}
      {scheduledEmails.length > 0 && (
        <div className="rounded-xl border p-4" style={{ background: theme.cardBg, borderColor: theme.border }}>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold flex items-center gap-2" style={{ color: theme.text }}>
              <Clock className="w-4 h-4" style={{ color: theme.secondary }} />
              Scheduled Emails ({scheduledEmails.length})
            </h3>
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" onClick={refreshScheduledEmails}>
                <RefreshCw className="w-4 h-4" />
              </Button>
              <Button size="sm" variant="ghost" onClick={handleClearScheduled} className="text-red-400">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {scheduledEmails.map((email, i) => (
              <div key={email.id || i} className="flex items-center justify-between p-2 rounded-lg text-sm" style={{ background: theme.bg }}>
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${email.status === 'sent' ? 'bg-green-500' : email.status === 'failed' ? 'bg-red-500' : 'bg-yellow-500'}`} />
                  <span style={{ color: theme.text }}>{email.from || 'Unknown'}</span>
                  <span style={{ color: theme.textDim }}>-</span>
                  <span style={{ color: theme.textMuted }}>{email.subject?.substring(0, 30)}...</span>
                </div>
                <span className="text-xs" style={{ color: theme.textDim }}>
                  {email.status === 'sent' ? '✓ Sent' : email.status === 'failed' ? '✗ Failed' : new Date(email.scheduled_time).toLocaleTimeString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Generated Emails */}
      {generatedEmails.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold flex items-center gap-2" style={{ color: theme.text }}>
            <Mail className="w-5 h-5" style={{ color: theme.primary }} />
            Generated Emails ({generatedEmails.length})
          </h3>
          
          <div className="space-y-4">
            {generatedEmails.map((email, index) => (
              <div 
                key={email.id || index}
                className="rounded-xl border overflow-hidden"
                style={{ background: theme.cardBg, borderColor: theme.border }}
              >
                {/* Email Header */}
                <div className="p-4 border-b flex flex-col sm:flex-row sm:items-center justify-between gap-3" style={{ borderColor: theme.border, background: theme.bg }}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: `${theme.secondary}30` }}>
                      <User className="w-5 h-5" style={{ color: theme.secondary }} />
                    </div>
                    <div>
                      <p className="font-semibold" style={{ color: theme.text }}>
                        {email.from_name} – {email.from_company}
                      </p>
                      <p className="text-xs" style={{ color: theme.textDim }}>
                        Category: {email.category?.replace(/_/g, ' ')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={() => handleSend(email, index)}
                      disabled={isSending[index]}
                      size="sm"
                      className="gap-1"
                      style={{ background: theme.success }}
                    >
                      {isSending[index] ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4" />
                      )}
                      Send
                    </Button>
                    <Button
                      onClick={() => deleteEmail(index)}
                      variant="ghost"
                      size="sm"
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Email Content */}
                <div className="p-4 space-y-3">
                  {/* Subject */}
                  <div>
                    <Label className="text-xs mb-1 block" style={{ color: theme.textMuted }}>Subject</Label>
                    <Input
                      value={email.subject}
                      onChange={(e) => updateEmail(index, 'subject', e.target.value)}
                      style={{ background: theme.bg, borderColor: theme.border, color: theme.text }}
                    />
                  </div>
                  
                  {/* Body */}
                  <div>
                    <Label className="text-xs mb-1 block" style={{ color: theme.textMuted }}>Body</Label>
                    <Textarea
                      value={email.body}
                      onChange={(e) => updateEmail(index, 'body', e.target.value)}
                      rows={6}
                      style={{ background: theme.bg, borderColor: theme.border, color: theme.text }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {generatedEmails.length === 0 && (
        <div className="rounded-xl border p-12 text-center" style={{ background: theme.cardBg, borderColor: theme.border }}>
          <Mail className="w-12 h-12 mx-auto mb-4" style={{ color: theme.textDim }} />
          <h3 className="text-lg font-semibold mb-2" style={{ color: theme.text }}>No Emails Generated</h3>
          <p className="text-sm" style={{ color: theme.textMuted }}>
            Click "Generate Client Email" to create simulated client emails
          </p>
        </div>
      )}

      {/* Info Box */}
      <div className="rounded-xl border p-4" style={{ background: `${theme.primary}10`, borderColor: `${theme.primary}30` }}>
        <p className="text-sm" style={{ color: theme.textMuted }}>
          <strong style={{ color: theme.primary }}>📧 Note:</strong> All generated emails will be sent to <strong>joe@birdenmedia.co</strong>. 
          The sender display name will show the persona or random client name.
        </p>
      </div>
    </div>
  );
};

// Main Dashboard
const AdminDashPage = () => {
  const [token, setToken] = useState(localStorage.getItem('admin_token'));
  const [stats, setStats] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('dashboard');
  const { showConfirm, ConfirmDialogComponent } = useConfirmDialog();

  const loadData = useCallback(async () => {
    if (!token) return;
    setIsLoading(true);
    try {
      const res = await adminApi.getGlobalStats(token);
      setStats(res.data);
      setOrganizations(res.data.all_organizations || []);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('admin_token');
        setToken(null);
      }
      toast.error('Failed to load data');
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    setToken(null);
  };

  const handleImpersonate = async (org) => {
    try {
      const res = await adminApi.getOrganization(org.id, token);
      const owner = res.data.users?.find(u => u.role === 'owner') || res.data.users?.[0];
      if (owner) {
        const impRes = await adminApi.impersonateUser(owner.id, token);
        if (impRes.data.success) {
          window.open(`/auth/verify?token=${impRes.data.impersonation_token}`, '_blank');
          toast.success(`Logged in as ${owner.name}`);
        }
      }
    } catch (error) {
      toast.error('Failed to impersonate');
    }
  };

  const handleSuspend = async (org) => {
    try {
      const newStatus = org.status === 'paused' ? 'active' : 'paused';
      await adminApi.updateOrganization(org.id, { status: newStatus }, token);
      toast.success(`Organization ${newStatus === 'paused' ? 'suspended' : 'reactivated'}`);
      loadData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleArchiveOrg = async (org) => {
    const confirmed = await showConfirm({
      title: `Archive "${org.company_name}"?`,
      description: 'They will be removed from your active client list but their records will be kept.',
      variant: 'archive',
      confirmText: 'Archive',
      cancelText: 'Cancel'
    });
    
    if (!confirmed) return;
    
    try {
      await adminApi.archiveOrganization(org.id, token);
      toast.success(`"${org.company_name}" archived`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to archive');
    }
  };

  const handleRestoreOrg = async (org) => {
    try {
      await adminApi.restoreOrganization(org.id, token);
      toast.success(`"${org.company_name}" restored`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore');
    }
  };

  const handlePermanentDeleteOrg = async (org) => {
    const firstConfirm = await showConfirm({
      title: `Delete "${org.company_name}"?`,
      description: 'This will permanently delete ALL their data including users, clients, estimates, invoices, and jobs. This action cannot be undone!',
      variant: 'danger',
      confirmText: 'Yes, Delete Everything',
      cancelText: 'Cancel',
      destructive: true
    });
    
    if (!firstConfirm) return;
    
    const secondConfirm = await showConfirm({
      title: 'Final Confirmation',
      description: `Are you ABSOLUTELY SURE you want to permanently delete "${org.company_name}"?`,
      variant: 'danger',
      confirmText: 'Delete Forever',
      cancelText: 'Cancel',
      destructive: true
    });
    
    if (!secondConfirm) return;
    
    try {
      await adminApi.deleteOrganization(org.id, token, true);
      toast.success(`"${org.company_name}" permanently deleted`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  // Separate active and archived organizations
  const activeOrgs = organizations.filter(org => org.status !== 'archived');
  const archivedOrgs = organizations.filter(org => org.status === 'archived');

  const filteredOrgs = activeOrgs.filter(org => 
    org.company_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    org.owner_email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (!token) return <AdminLogin onLogin={setToken} />;
  if (selectedOrg) return <OrgDetailView org={selectedOrg} token={token} onClose={() => setSelectedOrg(null)} onUpdated={loadData} />;
  
  // Show loading state while initial data is fetching
  if (isLoading && !stats) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: theme.bg }}>
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-t-transparent rounded-full animate-spin mx-auto mb-4" style={{ borderColor: theme.primary, borderTopColor: 'transparent' }}></div>
          <p style={{ color: theme.textMuted }}>Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen overflow-x-hidden" style={{ background: theme.bg }}>
      {/* Header - Mobile Responsive */}
      <header className="sticky top-0 z-40 border-b" style={{ background: `${theme.bg}ee`, borderColor: theme.border, backdropFilter: 'blur(8px)' }}>
        <div className="max-w-7xl mx-auto px-3 sm:px-6 h-14 sm:h-16 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center overflow-hidden" style={{ background: theme.primary }}>
              <img 
                src="/assets/birdenmedia-logo-white.png" 
                alt="BirdenMedia" 
                className="w-6 h-6 sm:w-7 sm:h-7 object-contain"
              />
            </div>
            <div>
              <h1 className="font-bold text-sm sm:text-base" style={{ color: theme.text }}>BirdenMedia</h1>
              <p className="text-xs hidden sm:block" style={{ color: theme.textDim }}>Admin Portal</p>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <Button onClick={() => setShowCreateModal(true)} size="sm" className="h-8 sm:h-9 px-2 sm:px-3 text-xs sm:text-sm" style={{ background: theme.primary }} data-testid="add-org-btn">
              <Plus className="w-4 h-4 sm:mr-1.5" /> 
              <span className="hidden sm:inline">Add Organization</span>
            </Button>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="h-8 sm:h-9 px-2" style={{ color: theme.textMuted }}>
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-3 sm:px-6 py-4 sm:py-6">
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <RefreshCw className="w-8 h-8 animate-spin" style={{ color: theme.primary }} />
          </div>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4 sm:space-y-6">
            <div className="overflow-x-auto -mx-3 px-3 sm:mx-0 sm:px-0">
              <TabsList className="border inline-flex w-max sm:w-auto" style={{ background: theme.cardBg, borderColor: theme.border }}>
                <TabsTrigger value="dashboard" className="data-[state=active]:bg-blue-500/10 data-[state=active]:text-blue-400 text-xs sm:text-sm px-3 sm:px-4">
                  <LayoutDashboard className="w-4 h-4 mr-1.5 sm:mr-2" /> <span className="hidden xs:inline">Dashboard</span>
                </TabsTrigger>
                <TabsTrigger value="archived" className="data-[state=active]:bg-amber-500/10 data-[state=active]:text-amber-400 text-xs sm:text-sm px-3 sm:px-4">
                  <Archive className="w-4 h-4 mr-1.5 sm:mr-2" /> <span className="hidden xs:inline">Archived</span> {archivedOrgs.length > 0 && `(${archivedOrgs.length})`}
                </TabsTrigger>
                <TabsTrigger value="analytics" className="data-[state=active]:bg-blue-500/10 data-[state=active]:text-blue-400 text-xs sm:text-sm px-3 sm:px-4">
                  <PieChart className="w-4 h-4 mr-1.5 sm:mr-2" /> <span className="hidden xs:inline">Analytics</span>
                </TabsTrigger>
                <TabsTrigger value="onboarding" className="data-[state=active]:bg-green-500/10 data-[state=active]:text-green-400 text-xs sm:text-sm px-3 sm:px-4">
                  <BookOpen className="w-4 h-4 mr-1.5 sm:mr-2" /> <span className="hidden xs:inline">Onboarding</span>
                </TabsTrigger>
                <TabsTrigger value="inquiries" className="data-[state=active]:bg-amber-500/10 data-[state=active]:text-amber-400 text-xs sm:text-sm px-3 sm:px-4" data-testid="admin-tab-inquiries">
                  <Mail className="w-4 h-4 mr-1.5 sm:mr-2" /> <span className="hidden xs:inline">Inquiries</span>
                </TabsTrigger>
                <TabsTrigger value="settings" className="data-[state=active]:bg-blue-500/10 data-[state=active]:text-blue-400 text-xs sm:text-sm px-3 sm:px-4">
                  <Settings className="w-4 h-4 mr-1.5 sm:mr-2" /> <span className="hidden xs:inline">Settings</span>
                </TabsTrigger>
                <TabsTrigger value="email-simulator" className="data-[state=active]:bg-purple-500/10 data-[state=active]:text-purple-400 text-xs sm:text-sm px-3 sm:px-4">
                  <Sparkles className="w-4 h-4 mr-1.5 sm:mr-2" /> <span className="hidden xs:inline">Email Sim</span>
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="dashboard" className="space-y-4 sm:space-y-6">
              {/* Platform Stats - Responsive Grid */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                <StatCard label="Organizations" value={stats?.platform?.total_organizations || 0} subValue={`${stats?.platform?.active || 0} active`} icon={Building2} />
                <StatCard label="Total Users" value={stats?.platform?.total_users || 0} icon={Users} />
                <StatCard label="Monthly Revenue" value={`$${(stats?.platform?.monthly_recurring_revenue || 0).toLocaleString()}`} subValue="Recurring" icon={DollarSign} />
                <StatCard label="Revenue Processed" value={`$${((stats?.contractor_aggregate?.revenue_processed || 0) / 1000).toFixed(1)}k`} subValue="By clients" icon={TrendingUp} />
              </div>

              {/* Organizations */}
              <div className="rounded-xl border" style={{ background: theme.cardBg, borderColor: theme.border }}>
                <div className="p-3 sm:p-5 border-b flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between" style={{ borderColor: theme.border }}>
                  <h2 className="font-semibold flex items-center gap-2 text-sm sm:text-base" style={{ color: theme.text }}>
                    <Building2 className="w-4 h-4 sm:w-5 sm:h-5" style={{ color: theme.primary }} />
                    Organizations ({filteredOrgs.length})
                  </h2>
                  <div className="relative w-full sm:w-64">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: theme.textDim }} />
                    <Input
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search..."
                      className="pl-9 h-9 border text-sm"
                      style={{ background: theme.bg, borderColor: theme.border, color: theme.text }}
                    />
                  </div>
                </div>
                <div className="p-3 sm:p-5 grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                  {filteredOrgs.map(org => (
                    <OrgCard key={org.id} org={org} onView={setSelectedOrg} onImpersonate={handleImpersonate} onSuspend={handleSuspend} onArchive={handleArchiveOrg} />
                  ))}
                  {filteredOrgs.length === 0 && (
                    <div className="col-span-1 md:col-span-2 py-12 text-center" style={{ color: theme.textDim }}>
                      <Building2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>No organizations found</p>
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

            {/* Archived Organizations Tab */}
            <TabsContent value="archived" className="space-y-4 sm:space-y-6">
              <div className="rounded-xl border" style={{ background: theme.cardBg, borderColor: theme.border }}>
                <div className="p-3 sm:p-5 border-b" style={{ borderColor: theme.border }}>
                  <h2 className="font-semibold flex items-center gap-2 text-sm sm:text-base" style={{ color: theme.text }}>
                    <Archive className="w-4 h-4 sm:w-5 sm:h-5" style={{ color: theme.secondary }} />
                    Archived Organizations ({archivedOrgs.length})
                  </h2>
                  <p className="text-xs sm:text-sm mt-1" style={{ color: theme.textMuted }}>
                    Archived clients are hidden from your active list but their records are preserved.
                  </p>
                </div>
                <div className="p-3 sm:p-5">
                  {archivedOrgs.length > 0 ? (
                    <div className="space-y-3">
                      {archivedOrgs.map(org => (
                        <div 
                          key={org.id}
                          className="rounded-lg p-4 border flex flex-col sm:flex-row sm:items-center justify-between gap-3"
                          style={{ background: theme.bg, borderColor: theme.border }}
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${theme.secondary}15` }}>
                              <Building2 className="w-5 h-5" style={{ color: theme.secondary }} />
                            </div>
                            <div>
                              <h3 className="font-semibold text-sm" style={{ color: theme.text }}>{org.company_name}</h3>
                              <p className="text-xs" style={{ color: theme.textMuted }}>
                                {org.owner_email} • Archived {org.archived_at ? formatDistanceToNow(new Date(org.archived_at), { addSuffix: true }) : ''}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleRestoreOrg(org)}
                              className="h-8 px-3 text-xs"
                              style={{ borderColor: theme.success, color: theme.success }}
                            >
                              <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
                              Restore
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handlePermanentDeleteOrg(org)}
                              className="h-8 px-3 text-xs"
                              style={{ borderColor: theme.danger, color: theme.danger }}
                            >
                              <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                              Delete Forever
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="py-12 text-center" style={{ color: theme.textDim }}>
                      <Archive className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p className="text-sm">No archived organizations</p>
                      <p className="text-xs mt-1">When you archive a client, they'll appear here</p>
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="analytics" className="space-y-4 sm:space-y-6">
              {/* Last 30 Days - Responsive Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                <StatCard label="New Organizations" value={stats?.last_30_days?.new_organizations || 0} subValue="Last 30 days" icon={Building2} />
                <StatCard label="Estimates Created" value={stats?.last_30_days?.estimates_created || 0} subValue="By all clients" icon={FileText} />
                <StatCard label="Invoices Paid" value={stats?.last_30_days?.invoices_paid || 0} subValue="By all clients" icon={Receipt} />
              </div>

              {/* Aggregate Stats */}
              <div className="rounded-xl p-4 sm:p-6 border" style={{ background: theme.cardBg, borderColor: theme.border }}>
                <h2 className="font-semibold mb-3 sm:mb-4 flex items-center gap-2 text-sm sm:text-base" style={{ color: theme.text }}>
                  <BarChart3 className="w-4 h-4 sm:w-5 sm:h-5" style={{ color: theme.primary }} />
                  Aggregate Platform Statistics
                </h2>
                <p className="text-xs sm:text-sm mb-4" style={{ color: theme.textMuted }}>Combined totals across all client organizations</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
                  {[
                    { label: 'Total Clients', value: stats?.contractor_aggregate?.total_clients },
                    { label: 'Total Estimates', value: stats?.contractor_aggregate?.total_estimates },
                    { label: 'Total Invoices', value: stats?.contractor_aggregate?.total_invoices },
                    { label: 'Total Jobs', value: stats?.contractor_aggregate?.total_jobs },
                    { label: 'Total Crew', value: stats?.contractor_aggregate?.total_employees },
                  ].map(({ label, value }) => (
                    <div key={label} className="p-3 sm:p-4 rounded-lg text-center" style={{ background: theme.bg }}>
                      <p className="text-lg sm:text-2xl font-bold" style={{ color: theme.text }}>{value || 0}</p>
                      <p className="text-xs sm:text-sm" style={{ color: theme.textDim }}>{label}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Breakdown by Relationship */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                <div className="rounded-xl p-4 sm:p-6 border" style={{ background: theme.cardBg, borderColor: theme.border }}>
                  <h3 className="font-semibold mb-3 sm:mb-4 text-sm sm:text-base" style={{ color: theme.text }}>By Relationship Type</h3>
                  <div className="space-y-2 sm:space-y-3">
                    {Object.entries(stats?.platform?.by_relationship || {}).map(([key, count]) => (
                      <div key={key} className="flex items-center justify-between p-2 sm:p-3 rounded-lg" style={{ background: theme.bg }}>
                        <Badge className={`${relationshipConfig[key]?.bgColor} ${relationshipConfig[key]?.textColor} text-xs`}>
                          {relationshipConfig[key]?.label || key}
                        </Badge>
                        <span className="font-semibold text-sm sm:text-base" style={{ color: theme.text }}>{count}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="rounded-xl p-4 sm:p-6 border" style={{ background: theme.cardBg, borderColor: theme.border }}>
                  <h3 className="font-semibold mb-3 sm:mb-4 text-sm sm:text-base" style={{ color: theme.text }}>By Industry</h3>
                  <div className="space-y-2 sm:space-y-3">
                    {Object.entries(stats?.platform?.by_industry || {}).map(([industry, count]) => (
                      <div key={industry} className="flex items-center justify-between p-2 sm:p-3 rounded-lg" style={{ background: theme.bg }}>
                        <span className="text-xs sm:text-sm" style={{ color: theme.textMuted }}>{industry}</span>
                        <span className="font-semibold text-sm sm:text-base" style={{ color: theme.text }}>{count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Onboarding Script Tab */}
            <TabsContent value="onboarding" className="space-y-4 sm:space-y-6">
              <div className="rounded-xl p-4 sm:p-6 border" style={{ background: theme.cardBg, borderColor: theme.border }}>
                <h2 className="font-semibold text-lg sm:text-xl mb-2 flex items-center gap-2" style={{ color: theme.text }}>
                  <BookOpen className="w-5 h-5" style={{ color: theme.success }} />
                  Client Onboarding Guide
                </h2>
                <p className="text-sm mb-6" style={{ color: theme.textMuted }}>
                  Use this script when onboarding new contractors. Check off each step as you complete it.
                </p>

                {/* Pre-Call Checklist */}
                <div className="mb-8">
                  <h3 className="font-semibold mb-3 flex items-center gap-2" style={{ color: theme.text }}>
                    <ClipboardList className="w-4 h-4" style={{ color: theme.primary }} />
                    Before the Onboarding Call
                  </h3>
                  <div className="space-y-2">
                    {[
                      'Create their organization in Admin Dashboard (Add Organization button)',
                      'Set their relationship type (BirdenMedia Client, Portal Only, or BM-Assist)',
                      'Set their service tier and monthly rate',
                      'Verify their login email was sent',
                      'Review their industry/services to prepare relevant tips',
                    ].map((item, i) => (
                      <label key={i} className="flex items-start gap-3 p-3 rounded-lg cursor-pointer hover:opacity-80" style={{ background: theme.bg }}>
                        <input type="checkbox" className="mt-1 w-4 h-4 rounded" />
                        <span className="text-sm" style={{ color: theme.textMuted }}>{item}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* During Call Script */}
                <div className="mb-8">
                  <h3 className="font-semibold mb-3 flex items-center gap-2" style={{ color: theme.text }}>
                    <Video className="w-4 h-4" style={{ color: theme.warning }} />
                    During the Onboarding Call (30 min)
                  </h3>
                  
                  <div className="space-y-4">
                    {/* Step 1 */}
                    <div className="p-4 rounded-lg border" style={{ background: theme.bg, borderColor: theme.border }}>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold" style={{ background: theme.primary, color: 'white' }}>1</span>
                        <span className="font-medium" style={{ color: theme.text }}>Login Together (2 min)</span>
                      </div>
                      <p className="text-sm ml-8" style={{ color: theme.textMuted }}>
                        "Let's log in together. Open the link I sent you..."<br/>
                        Help them bookmark or add to home screen on mobile.
                      </p>
                    </div>

                    {/* Step 2 */}
                    <div className="p-4 rounded-lg border" style={{ background: theme.bg, borderColor: theme.border }}>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold" style={{ background: theme.primary, color: 'white' }}>2</span>
                        <span className="font-medium" style={{ color: theme.text }}>Company Profile (5 min)</span>
                      </div>
                      <p className="text-sm ml-8" style={{ color: theme.textMuted }}>
                        "First, let's set up your company info. Go to Settings..."<br/>
                        • Company name, phone, email, address<br/>
                        • License number (if applicable)<br/>
                        • Toggle "Licensed & Insured"<br/>
                        • Select services offered
                      </p>
                    </div>

                    {/* Step 3 */}
                    <div className="p-4 rounded-lg border" style={{ background: theme.bg, borderColor: theme.border }}>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold" style={{ background: theme.primary, color: 'white' }}>3</span>
                        <span className="font-medium" style={{ color: theme.text }}>Payment Methods (5 min)</span>
                      </div>
                      <p className="text-sm ml-8" style={{ color: theme.textMuted }}>
                        "Now let's set up how customers can pay you. Go to Payments tab..."<br/>
                        • Toggle on: Check, Zelle, Cash (most common for contractors)<br/>
                        • Enter their Zelle email/phone<br/>
                        • Enter check payable info<br/>
                        • Add payment instructions (e.g., "50% deposit required")
                      </p>
                    </div>

                    {/* Step 4 */}
                    <div className="p-4 rounded-lg border" style={{ background: theme.bg, borderColor: theme.border }}>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold" style={{ background: theme.primary, color: 'white' }}>4</span>
                        <span className="font-medium" style={{ color: theme.text }}>Add Test Customer (3 min)</span>
                      </div>
                      <p className="text-sm ml-8" style={{ color: theme.textMuted }}>
                        "Let's add a test customer so you can see how it works..."<br/>
                        • Go to Customers → New Customer<br/>
                        • Use a real upcoming job if they have one
                      </p>
                    </div>

                    {/* Step 5 */}
                    <div className="p-4 rounded-lg border" style={{ background: theme.bg, borderColor: theme.border }}>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold" style={{ background: theme.primary, color: 'white' }}>5</span>
                        <span className="font-medium" style={{ color: theme.text }}>Create Site Visit (5 min)</span>
                      </div>
                      <p className="text-sm ml-8" style={{ color: theme.textMuted }}>
                        "When you get a new lead, you schedule a site visit..."<br/>
                        • New Site Visit → Select customer → Set date/time<br/>
                        • Show them Field Notes feature<br/>
                        • Explain offline mode for job sites
                      </p>
                    </div>

                    {/* Step 6 */}
                    <div className="p-4 rounded-lg border" style={{ background: theme.bg, borderColor: theme.border }}>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold" style={{ background: theme.primary, color: 'white' }}>6</span>
                        <span className="font-medium" style={{ color: theme.text }}>Create Estimate (5 min)</span>
                      </div>
                      <p className="text-sm ml-8" style={{ color: theme.textMuted }}>
                        "After your site visit, you create the estimate..."<br/>
                        • Convert to Estimate → Add line items<br/>
                        • Show them how customer receives and approves<br/>
                        • Explain the Convert to Job flow
                      </p>
                    </div>

                    {/* Step 7 */}
                    <div className="p-4 rounded-lg border" style={{ background: theme.bg, borderColor: theme.border }}>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold" style={{ background: theme.primary, color: 'white' }}>7</span>
                        <span className="font-medium" style={{ color: theme.text }}>Answer Questions (5 min)</span>
                      </div>
                      <p className="text-sm ml-8" style={{ color: theme.textMuted }}>
                        Common questions:<br/>
                        • "How do I add crew members?" → Settings → Team<br/>
                        • "Can I customize the estimate?" → Yes, templates in Settings<br/>
                        • "What if I'm offline?" → Field notes save locally
                      </p>
                    </div>
                  </div>
                </div>

                {/* Post-Call Checklist */}
                <div className="mb-8">
                  <h3 className="font-semibold mb-3 flex items-center gap-2" style={{ color: theme.text }}>
                    <Mail className="w-4 h-4" style={{ color: theme.success }} />
                    After the Call
                  </h3>
                  <div className="space-y-2">
                    {[
                      'Send follow-up email with portal URL and login',
                      'Remind them about the Help Guide in the app',
                      'Schedule 1-week check-in call',
                      'Add notes to their org in Admin Dashboard',
                    ].map((item, i) => (
                      <label key={i} className="flex items-start gap-3 p-3 rounded-lg cursor-pointer hover:opacity-80" style={{ background: theme.bg }}>
                        <input type="checkbox" className="mt-1 w-4 h-4 rounded" />
                        <span className="text-sm" style={{ color: theme.textMuted }}>{item}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Email Template */}
                <div>
                  <h3 className="font-semibold mb-3 flex items-center gap-2" style={{ color: theme.text }}>
                    <MessageSquare className="w-4 h-4" style={{ color: theme.primary }} />
                    Follow-Up Email Template
                  </h3>
                  <div className="p-4 rounded-lg text-sm font-mono" style={{ background: theme.bg, color: theme.textMuted }}>
                    <p><strong style={{ color: theme.text }}>Subject:</strong> Welcome to ContractorOS - Your Portal is Ready!</p>
                    <br/>
                    <p>Hey [Name],</p>
                    <br/>
                    <p>Great talking with you! Here's everything you need:</p>
                    <br/>
                    <p>🔗 <strong style={{ color: theme.text }}>Your Portal:</strong> [URL]</p>
                    <p>📧 <strong style={{ color: theme.text }}>Login:</strong> [email]</p>
                    <br/>
                    <p>📱 <strong style={{ color: theme.text }}>Mobile Tip:</strong> Add to your phone's home screen for quick access at job sites!</p>
                    <br/>
                    <p>📖 <strong style={{ color: theme.text }}>Need Help?</strong> Click "Help" in the sidebar for guides and tips.</p>
                    <br/>
                    <p>I'll check in with you next [day] to see how things are going. Just reply to this email or call/text me anytime!</p>
                    <br/>
                    <p>[Your name]<br/>BirdenMedia</p>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Inquiries Tab */}
            <TabsContent value="inquiries" className="space-y-6">
              <InquiriesPanel token={token} theme={theme} />
            </TabsContent>

            {/* Settings Tab */}
            <TabsContent value="settings" className="space-y-6">
              <PlatformSettingsPanel token={token} />
            </TabsContent>

            {/* Email Simulator Tab */}
            <TabsContent value="email-simulator" className="space-y-6">
              <EmailSimulatorPanel token={token} />
            </TabsContent>
          </Tabs>
        )}
      </main>

      <CreateOrgModal open={showCreateModal} onClose={() => setShowCreateModal(false)} onCreated={loadData} token={token} />
      
      {/* Confirm Dialog */}
      {ConfirmDialogComponent}
    </div>
  );
};

export default AdminDashPage;
